import { useState, useEffect, useRef, type CSSProperties } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { validatePasswordStrength, isPasswordValid, type PasswordValidation } from '@neural-intelligence/shared';
import { useAuth } from '../AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useDirtyForm } from '../hooks/useDirtyForm';
import { useNavigationGuard } from '../hooks/useNavigationGuard';
import EmailResponderInbox from '../components/EmailResponderInbox';
import EmailDraftsLog from '../components/EmailDraftsLog';
import AppHeader from '../components/AppHeader';
import HeaderUtilityActions from '../components/HeaderUtilityActions';
import {
  getChatbot,
  updateChatbot,
  addDomain,
  removeDomain,
  uploadPDF,
  getChatbotFiles,
  deleteFile,
  getFAQs,
  createFAQ,
  updateFAQ,
  deleteFAQ,
  extractWebsiteInfo,
  addReportEmail,
  removeReportEmail,
  getChatbotStats,
  getChatbotDailyStats,
  getChatbotMessageStats,
  getChatbotEmailDraftStats,
  deleteChatbot,
  getEmailConnection,
  getEmailConnectionDiagnostics,
  createEmailDiagnosticTest,
  checkEmailDiagnosticTest,
  testEmailDraftCreation,
  connectEmail,
  disconnectEmail,
  getEmailResponderConfig,
  updateEmailResponderConfig,
  getTrainingEmails,
  addTrainingEmail,
  deleteTrainingEmail,
  uploadTrainingEmailFiles,
  getChatbotCustomers,
  deleteChatbotCustomer,
  createCustomer,
  inviteUser,
  FAQ,
  EmailConnectionResponse,
  EmailConnectionDiagnostics,
  EmailDiagnosticTest,
  EmailDiagnosticTestCheck,
  EmailDraftCreationTest,
  EmailResponderConfig,
  TrainingEmail as TrainingEmailType,
  ChatbotCustomer,
  DailyCount,
  MessageStats,
  EmailDraftStats,
} from '../api';
import BarChart from '../components/BarChart';
import InfoTip from '../components/InfoTip';
import SetupProgress from '../components/SetupProgress';
import WelcomeGuide from '../components/WelcomeGuide';

// Theme presets (copied from shared to avoid build issues)
const THEME_PRESETS = {
  blue: {
    name: 'Blue (Default)',
    primaryColor: '#0066cc',
    headerBgColor: '#0066cc',
    headerTextColor: '#ffffff',
    chatBgColor: '#ffffff',
    userBubbleColor: '#0066cc',
    userTextColor: '#ffffff',
    assistantBubbleColor: '#f0f0f0',
    assistantTextColor: '#333333',
    headerSize: 'comfortable',
    logoLayout: 'badge',
    wideLogoAlign: 'center',
    logoSize: 'large',
    logoShape: 'rounded',
    logoBackground: 'subtle',
    launcherStyle: 'icon',
    panelSize: 'standard',
  },
  green: {
    name: 'Green',
    primaryColor: '#10b981',
    headerBgColor: '#10b981',
    headerTextColor: '#ffffff',
    chatBgColor: '#ffffff',
    userBubbleColor: '#10b981',
    userTextColor: '#ffffff',
    assistantBubbleColor: '#ecfdf5',
    assistantTextColor: '#065f46',
    headerSize: 'comfortable',
    logoLayout: 'badge',
    wideLogoAlign: 'center',
    logoSize: 'large',
    logoShape: 'rounded',
    logoBackground: 'subtle',
    launcherStyle: 'icon',
    panelSize: 'standard',
  },
  purple: {
    name: 'Purple',
    primaryColor: '#8b5cf6',
    headerBgColor: '#8b5cf6',
    headerTextColor: '#ffffff',
    chatBgColor: '#ffffff',
    userBubbleColor: '#8b5cf6',
    userTextColor: '#ffffff',
    assistantBubbleColor: '#f5f3ff',
    assistantTextColor: '#5b21b6',
    headerSize: 'comfortable',
    logoLayout: 'badge',
    wideLogoAlign: 'center',
    logoSize: 'large',
    logoShape: 'rounded',
    logoBackground: 'subtle',
    launcherStyle: 'icon',
    panelSize: 'standard',
  },
  red: {
    name: 'Red',
    primaryColor: '#ef4444',
    headerBgColor: '#ef4444',
    headerTextColor: '#ffffff',
    chatBgColor: '#ffffff',
    userBubbleColor: '#ef4444',
    userTextColor: '#ffffff',
    assistantBubbleColor: '#fef2f2',
    assistantTextColor: '#991b1b',
    headerSize: 'comfortable',
    logoLayout: 'badge',
    wideLogoAlign: 'center',
    logoSize: 'large',
    logoShape: 'rounded',
    logoBackground: 'subtle',
    launcherStyle: 'icon',
    panelSize: 'standard',
  },
  orange: {
    name: 'Orange',
    primaryColor: '#f97316',
    headerBgColor: '#f97316',
    headerTextColor: '#ffffff',
    chatBgColor: '#ffffff',
    userBubbleColor: '#f97316',
    userTextColor: '#ffffff',
    assistantBubbleColor: '#fff7ed',
    assistantTextColor: '#9a3412',
    headerSize: 'comfortable',
    logoLayout: 'badge',
    wideLogoAlign: 'center',
    logoSize: 'large',
    logoShape: 'rounded',
    logoBackground: 'subtle',
    launcherStyle: 'icon',
    panelSize: 'standard',
  },
  dark: {
    name: 'Dark',
    primaryColor: '#1f2937',
    headerBgColor: '#1f2937',
    headerTextColor: '#ffffff',
    chatBgColor: '#ffffff',
    userBubbleColor: '#374151',
    userTextColor: '#ffffff',
    assistantBubbleColor: '#f3f4f6',
    assistantTextColor: '#1f2937',
    headerSize: 'comfortable',
    logoLayout: 'badge',
    wideLogoAlign: 'center',
    logoSize: 'large',
    logoShape: 'rounded',
    logoBackground: 'subtle',
    launcherStyle: 'icon',
    panelSize: 'standard',
  },
} as const;

type ThemePresetKey = keyof typeof THEME_PRESETS;

type HeaderSize = 'legacy' | 'compact' | 'comfortable' | 'showcase';
type LogoLayout = 'badge' | 'wide';
type WideLogoAlign = 'center' | 'left';
type LogoSize = 'legacy' | 'small' | 'medium' | 'large';
type LogoShape = 'square' | 'rounded' | 'circle';
type LogoBackground = 'none' | 'subtle' | 'solid';
type LauncherStyle = 'icon' | 'logo' | 'logo-with-chat';
type PanelSize = 'legacy' | 'compact' | 'standard' | 'large';

interface Theme {
  primaryColor: string;
  headerBgColor: string;
  headerTextColor: string;
  chatBgColor: string;
  userBubbleColor: string;
  userTextColor: string;
  assistantBubbleColor: string;
  assistantTextColor: string;
  headerSize: HeaderSize;
  logoLayout: LogoLayout;
  wideLogoAlign: WideLogoAlign;
  logoSize: LogoSize;
  logoShape: LogoShape;
  logoBackground: LogoBackground;
  launcherStyle: LauncherStyle;
  panelSize: PanelSize;
}

type ThemeColorKey = keyof Pick<Theme, 'primaryColor' | 'headerBgColor' | 'headerTextColor' | 'chatBgColor' | 'userBubbleColor' | 'userTextColor' | 'assistantBubbleColor' | 'assistantTextColor'>;

const DEFAULT_THEME: Theme = {
  primaryColor: '#0066cc',
  headerBgColor: '#0066cc',
  headerTextColor: '#ffffff',
  chatBgColor: '#ffffff',
  userBubbleColor: '#0066cc',
  userTextColor: '#ffffff',
  assistantBubbleColor: '#f0f0f0',
  assistantTextColor: '#333333',
  headerSize: 'legacy',
  logoLayout: 'badge',
  wideLogoAlign: 'center',
  logoSize: 'legacy',
  logoShape: 'square',
  logoBackground: 'none',
  launcherStyle: 'icon',
  panelSize: 'legacy',
};

const RECOMMENDED_BRANDING: Pick<Theme, 'headerSize' | 'logoLayout' | 'wideLogoAlign' | 'logoSize' | 'logoShape' | 'logoBackground' | 'launcherStyle' | 'panelSize'> = {
  headerSize: 'comfortable',
  logoLayout: 'badge',
  wideLogoAlign: 'center',
  logoSize: 'large',
  logoShape: 'rounded',
  logoBackground: 'subtle',
  launcherStyle: 'icon',
  panelSize: 'standard',
};

const THEME_COLOR_KEYS: ThemeColorKey[] = [
  'primaryColor',
  'headerBgColor',
  'headerTextColor',
  'chatBgColor',
  'userBubbleColor',
  'userTextColor',
  'assistantBubbleColor',
  'assistantTextColor',
];

const CUSTOM_COLOR_FIELDS: Array<[ThemeColorKey, string]> = [
  ['primaryColor', 'Launcher Color'],
  ['headerBgColor', 'Header Background'],
  ['headerTextColor', 'Header Text'],
  ['chatBgColor', 'Chat Background'],
  ['userBubbleColor', 'User Bubble'],
  ['userTextColor', 'User Text'],
  ['assistantBubbleColor', 'Assistant Bubble'],
  ['assistantTextColor', 'Assistant Text'],
];

function normalizeTheme(value: Partial<Theme> | null | undefined): Theme {
  return { ...DEFAULT_THEME, ...(value || {}) };
}

const HEADER_SIZE_OPTIONS: Array<{ value: HeaderSize; label: string }> = [
  { value: 'legacy', label: 'Original' },
  { value: 'compact', label: 'Compact' },
  { value: 'comfortable', label: 'Comfortable' },
  { value: 'showcase', label: 'Showcase' },
];
const LOGO_LAYOUT_OPTIONS: Array<{ value: LogoLayout; label: string }> = [
  { value: 'badge', label: 'Logo + Title' },
  { value: 'wide', label: 'Wide Logo Only' },
];
const WIDE_LOGO_ALIGN_OPTIONS: Array<{ value: WideLogoAlign; label: string }> = [
  { value: 'center', label: 'Centered' },
  { value: 'left', label: 'Sticky Left' },
];
const LOGO_SIZE_OPTIONS: Array<{ value: LogoSize; label: string }> = [
  { value: 'legacy', label: 'Original' },
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
];
const LOGO_SHAPE_OPTIONS: Array<{ value: LogoShape; label: string }> = [
  { value: 'square', label: 'Square' },
  { value: 'rounded', label: 'Rounded' },
  { value: 'circle', label: 'Circle' },
];
const LOGO_BACKGROUND_OPTIONS: Array<{ value: LogoBackground; label: string }> = [
  { value: 'none', label: 'None' },
  { value: 'subtle', label: 'Subtle' },
  { value: 'solid', label: 'Solid' },
];
const LAUNCHER_STYLE_OPTIONS: Array<{ value: LauncherStyle; label: string }> = [
  { value: 'icon', label: 'Chat Icon' },
  { value: 'logo', label: 'Logo' },
  { value: 'logo-with-chat', label: 'Logo + Chat' },
];
const PANEL_SIZE_OPTIONS: Array<{ value: PanelSize; label: string }> = [
  { value: 'legacy', label: 'Original' },
  { value: 'compact', label: 'Compact' },
  { value: 'standard', label: 'Standard' },
  { value: 'large', label: 'Large' },
];

const HEADER_HEIGHTS: Record<HeaderSize, number> = { legacy: 60, compact: 56, comfortable: 72, showcase: 88 };
const PREVIEW_LOGO_SIZES: Record<HeaderSize, Record<LogoSize, number>> = {
  legacy: { legacy: 28, small: 28, medium: 28, large: 28 },
  compact: { legacy: 28, small: 28, medium: 32, large: 36 },
  comfortable: { legacy: 28, small: 32, medium: 36, large: 40 },
  showcase: { legacy: 28, small: 36, medium: 44, large: 52 },
};

interface ChatbotData {
  id: string;
  name: string;
  title: string | null;
  instructions: string;
  cta_label: string | null;
  cta_url: string | null;
  greeting: string;
  theme_json: string | null;
  logo_url: string | null;
  website_url: string | null;
  vector_store_id: string | null;
  domains: string[];
  report_emails?: string[];
  editable_by_sales?: boolean;
  is_testing?: boolean;
  customer_chatbot_enabled?: boolean;
  customer_email_responder_enabled?: boolean;
  analytics_enabled?: boolean;
}

interface FileData {
  id: string;
  filename: string;
  status: string;
  created_at: string;
}

interface PdfUploadSelection {
  validFiles: File[];
  invalidFileNames: string[];
  oversizedFileNames: string[];
}

export type TopTab = 'config' | 'chatbot' | 'knowledge-base' | 'email-responder';
const tabLabels: Record<TopTab, string> = { 'config': 'Admin Config', 'chatbot': 'Chatbot', 'knowledge-base': 'Knowledge Base', 'email-responder': 'Email Responder' };
const MAX_KNOWLEDGE_BASE_PDF_SIZE_MB = 15;
const MAX_KNOWLEDGE_BASE_PDF_SIZE_BYTES = MAX_KNOWLEDGE_BASE_PDF_SIZE_MB * 1024 * 1024;
const MAX_TRAINING_PDF_SIZE_MB = 10;
const MAX_TRAINING_PDF_SIZE_BYTES = MAX_TRAINING_PDF_SIZE_MB * 1024 * 1024;

function DraftBanner({ draft, message, onRestore, onDismiss }: {
  draft: Record<string, string> | null;
  message: string;
  onRestore: (draft: Record<string, string>) => void;
  onDismiss: () => void;
}) {
  if (!draft) return null;
  return (
    <div className="draft-recovery-banner">
      <span>{message}</span>
      <button type="button" className="btn btn-primary btn-small" onClick={() => onRestore(draft)}>Restore</button>
      <button type="button" className="btn btn-ghost btn-small" onClick={onDismiss}>Dismiss</button>
    </div>
  );
}

export default function ChatbotDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { toast, confirm } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const skipGuardRef = useRef(false);
  const emailDataPreloaded = useRef(false);
  const isCustomer = user?.role === 'customer';
  const isSales = user?.role === 'sales';
  const isAdmin = user?.role === 'admin';
  const cl = (admin: string, customer: string) => isCustomer ? customer : admin;

  const [showWelcome, setShowWelcome] = useState(() => {
    if (!isCustomer || !user?.id) return false;
    return !localStorage.getItem(`ni-welcome-seen-${user.id}`);
  });
  const [showWidgetHint, setShowWidgetHint] = useState(() => {
    if (!isCustomer || !user?.id) return false;
    return !localStorage.getItem(`ni-widget-hint-seen-${user.id}`);
  });

  // Top-level tab from URL
  const tabParam = searchParams.get('tab') as TopTab | null;
  const allTabs: TopTab[] = ['config', 'chatbot', 'knowledge-base', 'email-responder'];
  const activeTab = tabParam && allTabs.includes(tabParam) ? tabParam : (isAdmin ? 'config' : 'chatbot');

  async function setActiveTab(tab: TopTab) {
    if (isAnyDirty) {
      const discard = await confirm({
        title: 'Unsaved Changes',
        description: 'You have unsaved changes. Switching tabs will discard them.',
        confirmLabel: 'Discard & Switch',
        cancelLabel: 'Stay',
        destructive: true,
      });
      if (!discard) return;
      // Reset form fields to saved values
      setName(savedValues.name); setTitle(savedValues.title);
      setGreeting(savedValues.greeting); setCtaLabel(savedValues.ctaLabel); setCtaUrl(savedValues.ctaUrl);
      setInstructions(savedValues.instructions); setWebsiteUrl(savedValues.websiteUrl);
      setLogoUrl(savedValues.logoUrl);
      try { setTheme(normalizeTheme(JSON.parse(savedValues.theme))); } catch {}
      if (savedEmailConfig && emailConfig) {
        setEmailConfig({
          ...emailConfig,
          response_tone: savedEmailConfig.response_tone as EmailResponderConfig['response_tone'],
          email_signature: savedEmailConfig.email_signature,
          scan_interval_minutes: Number(savedEmailConfig.scan_interval_minutes),
          cc_forwarder_on_forward: savedEmailConfig.cc_forwarder_on_forward === 'true',
          exclude_newsletters: savedEmailConfig.exclude_newsletters !== 'false',
        });
      }
      chatbotDirty.clearDraft(); appearanceDirty.clearDraft(); knowledgeDirty.clearDraft(); emailConfigDirty.clearDraft();
    }
    setSearchParams({ tab }, { replace: true });
  }

  const [chatbot, setChatbot] = useState<ChatbotData | null>(null);

  // Compute visible tabs based on role
  const visibleTabs: TopTab[] = (() => {
    if (isCustomer) {
      if (!chatbot) return ['chatbot', 'knowledge-base', 'email-responder']; // Show all until loaded, then redirect
      const tabs: TopTab[] = [];
      if (chatbot.customer_chatbot_enabled !== false) tabs.push('chatbot');
      tabs.push('knowledge-base'); // Always visible
      if (chatbot.customer_email_responder_enabled) tabs.push('email-responder');
      return tabs.length > 0 ? tabs : ['knowledge-base'];
    }
    if (isSales) return allTabs.filter(t => t !== 'config');
    return allTabs;
  })();

  const [files, setFiles] = useState<FileData[]>([]);
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [stats, setStats] = useState<{ total: number; last24h: number; last7d: number; last30d: number } | null>(null);
  const [dailyStats, setDailyStats] = useState<DailyCount[]>([]);
  const [messageStats, setMessageStats] = useState<MessageStats | null>(null);
  const [emailDraftStats, setEmailDraftStats] = useState<EmailDraftStats | null>(null);
  const [showAnalyticsPreview, setShowAnalyticsPreview] = useState(false);
  const [widgetKey, setWidgetKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [uploadingCount, setUploadingCount] = useState(0);
  const [saveMessage, setSaveMessage] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [copied, setCopied] = useState(false);

  // Toast notifications
  const [domainToast, setDomainToast] = useState<{ message: string; type: 'saving' | 'success' | 'error' } | null>(null);
  const [appearanceToast, setAppearanceToast] = useState<{ message: string; type: 'error' | 'warning' } | null>(null);

  // Track saved values for unsaved changes detection
  const [savedValues, setSavedValues] = useState({
    name: '', title: '', instructions: '', greeting: '', ctaLabel: '', ctaUrl: '', websiteUrl: '', logoUrl: '', theme: JSON.stringify(DEFAULT_THEME)
  });

  // Form fields
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [instructions, setInstructions] = useState('');
  const [greeting, setGreeting] = useState('');
  const [ctaLabel, setCtaLabel] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');

  // Appearance fields
  const [logoUrl, setLogoUrl] = useState('');
  const [selectedPreset, setSelectedPreset] = useState<ThemePresetKey | 'custom'>('blue');
  const [theme, setTheme] = useState<Theme>(DEFAULT_THEME);
  const [appearanceSaving, setAppearanceSaving] = useState(false);
  const [appearanceMessage, setAppearanceMessage] = useState('');

  // FAQ fields
  const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);
  const [newFaqQuestion, setNewFaqQuestion] = useState('');
  const [newFaqAnswer, setNewFaqAnswer] = useState('');
  const [faqSaving, setFaqSaving] = useState(false);

  // Website extraction
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState('');
  const [showExtractConfirm, setShowExtractConfirm] = useState(false);
  const [pendingExtractedContent, setPendingExtractedContent] = useState('');

  // Email Responder state
  const [emailConnection, setEmailConnection] = useState<EmailConnectionResponse | null>(null);
  const [emailDiagnostics, setEmailDiagnostics] = useState<EmailConnectionDiagnostics | null>(null);
  const [emailDiagnosticsLoading, setEmailDiagnosticsLoading] = useState(false);
  const [emailDiagnosticsError, setEmailDiagnosticsError] = useState('');
  const [emailDiagnosticTest, setEmailDiagnosticTest] = useState<EmailDiagnosticTest | null>(null);
  const [emailDiagnosticTestCheck, setEmailDiagnosticTestCheck] = useState<EmailDiagnosticTestCheck | null>(null);
  const [emailDiagnosticTestLoading, setEmailDiagnosticTestLoading] = useState(false);
  const [emailDiagnosticTestChecking, setEmailDiagnosticTestChecking] = useState(false);
  const [emailDiagnosticTestError, setEmailDiagnosticTestError] = useState('');
  const [emailDraftTest, setEmailDraftTest] = useState<EmailDraftCreationTest | null>(null);
  const [emailDraftTestLoading, setEmailDraftTestLoading] = useState(false);
  const [emailDraftTestError, setEmailDraftTestError] = useState('');
  const [connectionMessage, setConnectionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [emailConfig, setEmailConfig] = useState<EmailResponderConfig | null>(null);
  const [savedEmailConfig, setSavedEmailConfig] = useState<Record<string, string> | null>(null);
  const [trainingEmails, setTrainingEmails] = useState<TrainingEmailType[]>([]);

  const [emailTabLoaded, setEmailTabLoaded] = useState(false);
  const [emailConfigSaving, setEmailConfigSaving] = useState(false);
  const [emailConfigMessage, setEmailConfigMessage] = useState('');
  const [connectingProvider, setConnectingProvider] = useState<string | null>(null);
  const [showAddTraining, setShowAddTraining] = useState(false);
  const [newTrainingSubject, setNewTrainingSubject] = useState('');
  const [newTrainingIncoming, setNewTrainingIncoming] = useState('');
  const [newTrainingResponse, setNewTrainingResponse] = useState('');
  const [trainingSaving, setTrainingSaving] = useState(false);
  const [expandedTrainingId, setExpandedTrainingId] = useState<string | null>(null);
  const [uploadingTraining, setUploadingTraining] = useState(false);
  const [trainingPdfUploadCount, setTrainingPdfUploadCount] = useState(0);
  const [isTrainingPdfDragging, setIsTrainingPdfDragging] = useState(false);
  const [uploadResult, setUploadResult] = useState<{ created: number; errors: string[] } | null>(null);
  const incomingFileRef = useRef<HTMLInputElement>(null);
  const responseFileRef = useRef<HTMLInputElement>(null);
  const pdfFileRef = useRef<HTMLInputElement>(null);
  // Customer management state (admin only)
  const [chatbotCustomers, setChatbotCustomers] = useState<ChatbotCustomer[]>([]);
  const [customersLoaded, setCustomersLoaded] = useState(false);
  const [showCreateCustomer, setShowCreateCustomer] = useState(false);
  const [newCustomerEmail, setNewCustomerEmail] = useState('');
  const [newCustomerPassword, setNewCustomerPassword] = useState('');
  const [customerSaving, setCustomerSaving] = useState(false);
  const [customerMessage, setCustomerMessage] = useState('');
  const [customerInviteMode, setCustomerInviteMode] = useState(true);
  const [custPwdValidation, setCustPwdValidation] = useState<PasswordValidation>({ length: false, uppercase: false, lowercase: false, number: false, special: false });
  const custPwdValid = isPasswordValid(custPwdValidation);

  // Email Responder toggle derived state
  const responderToggleDisabled = emailConfigSaving || !emailConnection?.connected || isSales;
  const responderHelperText = !emailConnection?.connected
    ? 'Connect an email account above to enable the email responder.'
    : isSales
      ? 'Only admins and customers can manage this setting.'
      : 'When enabled, automatically scans the connected inbox for new emails and generates AI draft replies using the chatbot\'s knowledge base.';
  const uploading = uploadingCount > 0;

  // Dirty form tracking
  const chatbotDirty = useDirtyForm({
    chatbotId: id!,
    formName: 'basic-info',
    savedValues: { name: savedValues.name, title: savedValues.title, greeting: savedValues.greeting, ctaLabel: savedValues.ctaLabel, ctaUrl: savedValues.ctaUrl },
    currentValues: { name, title, greeting, ctaLabel, ctaUrl },
    enabled: !loading,
  });
  const appearanceDirty = useDirtyForm({
    chatbotId: id!,
    formName: 'appearance',
    savedValues: { logoUrl: savedValues.logoUrl, theme: savedValues.theme },
    currentValues: { logoUrl, theme: JSON.stringify(theme) },
    enabled: !loading,
  });
  const knowledgeDirty = useDirtyForm({
    chatbotId: id!,
    formName: 'knowledge',
    savedValues: { instructions: savedValues.instructions, websiteUrl: savedValues.websiteUrl },
    currentValues: { instructions, websiteUrl },
    enabled: !loading,
  });
  const emailConfigDirty = useDirtyForm({
    chatbotId: id!,
    formName: 'email-config',
    savedValues: savedEmailConfig ?? { response_tone: '', email_signature: '', scan_interval_minutes: '', cc_forwarder_on_forward: 'false', exclude_newsletters: 'true' },
    currentValues: emailConfig ? {
      response_tone: emailConfig.response_tone || '',
      email_signature: emailConfig.email_signature || '',
      scan_interval_minutes: String(emailConfig.scan_interval_minutes ?? ''),
      cc_forwarder_on_forward: String(Boolean(emailConfig.cc_forwarder_on_forward)),
      exclude_newsletters: String(emailConfig.exclude_newsletters ?? true),
    } : { response_tone: '', email_signature: '', scan_interval_minutes: '', cc_forwarder_on_forward: 'false', exclude_newsletters: 'true' },
    enabled: !!emailConfig && !!savedEmailConfig,
  });

  const isAnyDirty = chatbotDirty.isDirty || appearanceDirty.isDirty || knowledgeDirty.isDirty || emailConfigDirty.isDirty;

  useNavigationGuard(isAnyDirty, confirm, skipGuardRef);

  useEffect(() => {
    if (!isAnyDirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isAnyDirty]);

  function tabIsDirty(tab: TopTab): boolean {
    switch (tab) {
      case 'chatbot': return chatbotDirty.isDirty || appearanceDirty.isDirty;
      case 'knowledge-base': return knowledgeDirty.isDirty;
      case 'email-responder': return emailConfigDirty.isDirty;
      default: return false;
    }
  }

  function restoreChatbotDraft(d: Record<string, string>) {
    if (d.name !== undefined) setName(d.name);
    if (d.title !== undefined) setTitle(d.title);
    if (d.greeting !== undefined) setGreeting(d.greeting);
    if (d.ctaLabel !== undefined) setCtaLabel(d.ctaLabel);
    if (d.ctaUrl !== undefined) setCtaUrl(d.ctaUrl);
    chatbotDirty.clearDraft();
  }

  function restoreAppearanceDraft(d: Record<string, string>) {
    if (d.logoUrl !== undefined) setLogoUrl(d.logoUrl);
    if (d.theme !== undefined) { try { setTheme(normalizeTheme(JSON.parse(d.theme))); setSelectedPreset('custom'); } catch {} }
    appearanceDirty.clearDraft();
  }

  function restoreKnowledgeDraft(d: Record<string, string>) {
    if (d.instructions !== undefined) setInstructions(d.instructions);
    if (d.websiteUrl !== undefined) setWebsiteUrl(d.websiteUrl);
    knowledgeDirty.clearDraft();
  }

  function restoreEmailConfigDraft(d: Record<string, string>) {
    if (emailConfig) {
      setEmailConfig({
        ...emailConfig,
        ...(d.response_tone !== undefined && { response_tone: d.response_tone as EmailResponderConfig['response_tone'] }),
        ...(d.email_signature !== undefined && { email_signature: d.email_signature }),
        ...(d.scan_interval_minutes !== undefined && { scan_interval_minutes: parseInt(d.scan_interval_minutes) || 5 }),
        ...(d.cc_forwarder_on_forward !== undefined && { cc_forwarder_on_forward: d.cc_forwarder_on_forward === 'true' }),
        ...(d.exclude_newsletters !== undefined && { exclude_newsletters: d.exclude_newsletters !== 'false' }),
      });
    }
    emailConfigDirty.clearDraft();
  }

  // Inject/remove chat widget based on active tab
  useEffect(() => {
    function cleanupWidget() {
      const script = document.getElementById('preview-widget-script');
      if (script) script.remove();
      document.querySelectorAll('.chat-widget-container').forEach(w => w.remove());
      document.querySelectorAll('.chat-widget-error-container').forEach(w => w.remove());
      document.querySelectorAll('style').forEach(s => { if (s.textContent?.includes('chat-widget')) s.remove(); });
    }

    if (activeTab === 'chatbot' && chatbot) {
      cleanupWidget();
      const script = document.createElement('script');
      script.id = 'preview-widget-script';
      const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;
      const widgetUrl = import.meta.env.VITE_WIDGET_URL || 'http://localhost:5174';
      script.src = `${widgetUrl}/chat-widget.js`;
      script.setAttribute('data-chatbot-id', chatbot.id);
      script.setAttribute('data-api-url', apiUrl);
      script.setAttribute('data-preview-mode', 'true');
      script.defer = true;
      document.body.appendChild(script);
    } else {
      cleanupWidget();
    }

    return cleanupWidget;
  }, [activeTab, chatbot?.id, widgetKey]);

  function dismissWidgetHint() {
    setShowWidgetHint(false);
    if (user?.id) localStorage.setItem(`ni-widget-hint-seen-${user.id}`, 'true');
  }

  // Auto-dismiss widget hint after 10 seconds
  useEffect(() => {
    if (!showWidgetHint) return;
    const timer = setTimeout(dismissWidgetHint, 10000);
    return () => clearTimeout(timer);
  }, [showWidgetHint]);

  // Redirect customer to first visible tab if on a hidden tab
  useEffect(() => {
    if (isCustomer && chatbot && !visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0]);
    }
  }, [isCustomer, chatbot, activeTab, visibleTabs]);

  useEffect(() => {
    if (id) {
      // Customer trying to access a chatbot that isn't theirs
      if (isCustomer && user?.assigned_chatbot_id && id !== user.assigned_chatbot_id) {
        navigate(`/chatbots/${user.assigned_chatbot_id}?tab=chatbot`, { replace: true });
        return;
      }
      loadChatbot();
      loadFiles();
      loadFaqs();
    }
  }, [id]);

  // Lazy load email responder data when tab is activated
  useEffect(() => {
    if (activeTab === 'email-responder' && id && !emailTabLoaded) {
      loadEmailResponderData();
    }
  }, [activeTab, id, emailTabLoaded]);

  // For customers: eagerly fetch email data so SetupProgress has accurate state
  useEffect(() => {
    if (!isCustomer || !id || emailTabLoaded) return;
    if (!visibleTabs.includes('email-responder')) return;
    emailDataPreloaded.current = true;
    Promise.all([
      getEmailConnection(id),
      getTrainingEmails(id),
    ]).then(([connRes, trainingRes]) => {
      if (connRes.success && connRes.data?.connected) setEmailConnection(connRes.data);
      if (trainingRes.success && trainingRes.data) setTrainingEmails(trainingRes.data);
    });
  }, [isCustomer, id]);

  // Load customers for this chatbot (admin only, on chatbot tab)
  useEffect(() => {
    if (user?.role === 'admin' && activeTab === 'config' && id && !customersLoaded) {
      getChatbotCustomers(id).then((res) => {
        if (res.success && res.data) setChatbotCustomers(res.data);
        setCustomersLoaded(true);
      });
    }
  }, [user?.role, activeTab, id, customersLoaded]);

  // Handle OAuth callback query params (?connection=success/error)
  useEffect(() => {
    const connection = searchParams.get('connection');
    if (!connection) return;
    const reason = searchParams.get('reason');
    if (connection === 'success') {
      setConnectionMessage({ type: 'success', text: 'Email account connected successfully!' });
      // Refresh connection data
      if (id) {
        getEmailConnection(id).then((res) => {
          if (res.success && res.data?.connected) setEmailConnection(res.data);
        });
      }
    } else if (connection === 'error') {
      const messages: Record<string, string> = {
        missing_params: 'Missing parameters from the email provider.',
        invalid_state: 'The connection link expired. Please try again.',
        provider_mismatch: 'Provider mismatch error. Please try again.',
        token_exchange_failed: 'Failed to authorize with the email provider. Please try again.',
        email_fetch_failed: 'Could not retrieve your email address. Please try again.',
        encryption_failed: 'Internal error. Please try again.',
        db_error: 'Failed to save the connection. Please try again.',
      };
      setConnectionMessage({ type: 'error', text: messages[reason || ''] || 'Failed to connect email account. Please try again.' });
    }
    // Clean query params
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('connection');
    newParams.delete('reason');
    setSearchParams(newParams, { replace: true });
    // Auto-dismiss success after 5 seconds
    if (connection === 'success') {
      setTimeout(() => setConnectionMessage(null), 5000);
    }
  }, []);

  // Poll for file status updates
  const isPollingFiles = files.some((f) => f.status === 'in_progress');
  useEffect(() => {
    if (!isPollingFiles || !id) return;
    const interval = setInterval(async () => {
      try {
        const result = await getChatbotFiles(id);
        if (result.success && result.data) {
          setFiles(result.data as FileData[]);
        }
      } catch (e) {}
    }, 3000);
    return () => clearInterval(interval);
  }, [isPollingFiles, id]);

  async function fetchAnalytics() {
    if (!id) return;
    const [statsResult, dailyResult, msgResult, draftResult] = await Promise.all([
      getChatbotStats(id),
      getChatbotDailyStats(id),
      getChatbotMessageStats(id),
      getChatbotEmailDraftStats(id),
    ]);
    if (statsResult.success && statsResult.data) setStats(statsResult.data);
    if (dailyResult.success && dailyResult.data) setDailyStats(dailyResult.data);
    if (msgResult.success && msgResult.data) setMessageStats(msgResult.data);
    if (draftResult.success && draftResult.data) setEmailDraftStats(draftResult.data);
  }

  async function loadChatbot() {
    try {
      const result = await getChatbot(id!);
      if (result.success && result.data) {
        const data = result.data;
        setChatbot(data);
        setName(data.name);
        setTitle(data.title || '');
        setInstructions(data.instructions);
        setGreeting(data.greeting);
        setCtaLabel(data.cta_label || '');
        setCtaUrl(data.cta_url || '');
        setLogoUrl(data.logo_url || '');
        setWebsiteUrl(data.website_url || '');

        let loadedTheme = DEFAULT_THEME;
        if (data.theme_json) {
          try {
            const parsedTheme = normalizeTheme(JSON.parse(data.theme_json) as Partial<Theme>);
            loadedTheme = parsedTheme;
            setTheme(parsedTheme);
            const matchingPreset = (Object.keys(THEME_PRESETS) as ThemePresetKey[]).find((key) => {
              const preset = THEME_PRESETS[key];
              return THEME_COLOR_KEYS.every((colorKey) => preset[colorKey] === parsedTheme[colorKey]);
            });
            setSelectedPreset(matchingPreset || 'custom');
          } catch {
            setTheme(DEFAULT_THEME);
            setSelectedPreset('blue');
          }
        } else {
          setTheme(DEFAULT_THEME);
          setSelectedPreset('blue');
        }

        setSavedValues({
          name: data.name, title: data.title || '', instructions: data.instructions, greeting: data.greeting,
          ctaLabel: data.cta_label || '', ctaUrl: data.cta_url || '', websiteUrl: data.website_url || '',
          logoUrl: data.logo_url || '', theme: JSON.stringify(loadedTheme)
        });

        if (user?.role === 'sales' && !data.editable_by_sales) {
          // Sales without edit permission — they can only view
        }
        if (data.analytics_enabled) await fetchAnalytics();
      } else {
        navigate('/');
      }
    } catch (e) {
      navigate('/');
    } finally {
      setLoading(false);
    }
  }

  async function loadFiles() {
    try {
      const result = await getChatbotFiles(id!);
      if (result.success && result.data) setFiles(result.data);
    } catch (e) {}
  }

  async function loadFaqs() {
    try {
      const result = await getFAQs(id!);
      if (result.success && result.data) setFaqs(result.data);
    } catch (e) {}
  }

  async function loadEmailResponderData() {
    try {
      const preloaded = emailDataPreloaded.current;
      const [connRes, configRes, trainingRes] = await Promise.all([
        preloaded ? null : getEmailConnection(id!),
        getEmailResponderConfig(id!),
        preloaded ? null : getTrainingEmails(id!),
      ]);
      emailDataPreloaded.current = false;
      if (connRes) {
        if (connRes.success && connRes.data?.connected) setEmailConnection(connRes.data);
        else setEmailConnection(null);
      }
      if (configRes.success && configRes.data) {
        setEmailConfig(configRes.data);
        setSavedEmailConfig({
          response_tone: configRes.data.response_tone || '',
          email_signature: configRes.data.email_signature || '',
          scan_interval_minutes: String(configRes.data.scan_interval_minutes ?? ''),
          cc_forwarder_on_forward: String(Boolean(configRes.data.cc_forwarder_on_forward)),
          exclude_newsletters: String(configRes.data.exclude_newsletters ?? true),
        });
      }
      if (trainingRes?.success && trainingRes.data) setTrainingEmails(trainingRes.data);
      setEmailTabLoaded(true);
    } catch (e) {
      setEmailTabLoaded(true);
    }
  }

  // ========== Chatbot Tab handlers ==========
  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setSaveMessage('');
    try {
      await updateChatbot(id!, { name, title: title || undefined, greeting, cta_label: ctaLabel.trim() || null, cta_url: ctaUrl.trim() || null });
      setSaveMessage('Saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
      setSavedValues(prev => ({ ...prev, name, title: title || '', greeting, ctaLabel: ctaLabel || '', ctaUrl: ctaUrl || '' }));
      chatbotDirty.clearDraft();
      setWidgetKey(k => k + 1);
    } catch (e) {
      setSaveMessage('Failed to save');
    } finally {
      setSaving(false);
    }
  }

  // ========== Knowledge Base Tab handlers ==========
  async function handleKnowledgeSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setSaveMessage('');
    try {
      await updateChatbot(id!, { instructions, website_url: websiteUrl || undefined });
      setSaveMessage('Saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
      setSavedValues(prev => ({ ...prev, instructions, websiteUrl: websiteUrl || '' }));
      knowledgeDirty.clearDraft();
    } catch (e) {
      setSaveMessage('Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleExtractWebsite() {
    if (!websiteUrl.trim()) return;
    setExtracting(true);
    setExtractError('');
    try {
      const result = await extractWebsiteInfo(id!, websiteUrl.trim());
      if (result.success && result.data) {
        if (instructions.trim()) {
          setPendingExtractedContent(result.data.description);
          setShowExtractConfirm(true);
        } else {
          setInstructions(result.data.description);
        }
      } else {
        setExtractError(result.error || 'Failed to extract website information');
      }
    } catch (e) {
      setExtractError('An unexpected error occurred');
    } finally {
      setExtracting(false);
    }
  }

  function handleExtractReplace() { setInstructions(pendingExtractedContent); setPendingExtractedContent(''); setShowExtractConfirm(false); }
  function handleExtractAppend() { setInstructions(instructions.trim() + '\n\n' + pendingExtractedContent); setPendingExtractedContent(''); setShowExtractConfirm(false); }
  function handleExtractCancel() { setPendingExtractedContent(''); setShowExtractConfirm(false); }

  function normalizeDomain(input: string): { domain: string; error?: string } {
    let domain = input.trim();
    if (!domain) return { domain: '', error: 'Domain cannot be empty' };
    domain = domain.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0].split(':')[0].trim();
    if (!domain || domain.length === 0) return { domain: '', error: 'Invalid domain format' };
    if (!domain.includes('.') && domain !== 'localhost') return { domain: '', error: 'Please enter a complete domain (e.g., example.com)' };
    return { domain };
  }

  async function handleAddDomain(e: React.FormEvent) {
    e.preventDefault();
    if (!newDomain.trim()) return;
    const { domain: baseDomain, error } = normalizeDomain(newDomain);
    if (error) { setDomainToast({ message: error, type: 'error' }); setTimeout(() => setDomainToast(null), 4000); return; }
    setDomainToast({ message: 'Adding domains...', type: 'saving' });
    const domainsToAdd = [baseDomain, '*.' + baseDomain];
    const addedDomains: string[] = [];
    let hasError = false;
    for (const domain of domainsToAdd) {
      if (chatbot!.domains.includes(domain)) continue;
      try { await addDomain(id!, domain); addedDomains.push(domain); }
      catch (e: any) { if (e.message?.includes('already exists')) continue; hasError = true; break; }
    }
    if (hasError) { setDomainToast({ message: 'Failed to add domains', type: 'error' }); setTimeout(() => setDomainToast(null), 4000); }
    else if (addedDomains.length > 0) {
      setChatbot({ ...chatbot!, domains: [...chatbot!.domains, ...addedDomains] });
      setNewDomain('');
      setDomainToast({ message: addedDomains.length === 2 ? `Added ${baseDomain} and *.${baseDomain}` : `Added ${addedDomains.join(', ')}`, type: 'success' });
      setTimeout(() => setDomainToast(null), 3000);
    } else { setDomainToast({ message: 'These domains are already in your list', type: 'error' }); setTimeout(() => setDomainToast(null), 3000); }
  }

  async function handleRemoveDomain(domain: string) {
    setDomainToast({ message: 'Removing domain...', type: 'saving' });
    try {
      await removeDomain(id!, domain);
      setChatbot({ ...chatbot!, domains: chatbot!.domains.filter((d) => d !== domain) });
      setDomainToast({ message: 'Domain removed successfully!', type: 'success' });
      setTimeout(() => setDomainToast(null), 3000);
    } catch (e) { setDomainToast({ message: 'Failed to remove domain', type: 'error' }); setTimeout(() => setDomainToast(null), 4000); }
  }

  function isPdfFile(file: File) {
    return file.type.toLowerCase().includes('pdf') || file.name.toLowerCase().endsWith('.pdf');
  }

  function getPdfUploadSelection(fileList: FileList | File[], maxSizeBytes: number): PdfUploadSelection {
    const validFiles: File[] = [];
    const invalidFileNames: string[] = [];
    const oversizedFileNames: string[] = [];

    for (const file of Array.from(fileList)) {
      if (!isPdfFile(file)) {
        invalidFileNames.push(file.name);
      } else if (file.size > maxSizeBytes) {
        oversizedFileNames.push(file.name);
      } else {
        validFiles.push(file);
      }
    }

    return { validFiles, invalidFileNames, oversizedFileNames };
  }

  function formatUploadErrors(errors: string[]) {
    if (errors.length === 0) return '';
    const visibleErrors = errors.slice(0, 2).join(' ');
    if (errors.length <= 2) return visibleErrors;
    const remainingErrors = errors.length - 2;
    return `${visibleErrors} ${remainingErrors} more file${remainingErrors === 1 ? '' : 's'} failed.`;
  }

  function buildUploadSummary(successCount: number, errors: string[]) {
    if (successCount > 0 && errors.length === 0) {
      return `${successCount} PDF${successCount === 1 ? '' : 's'} uploaded successfully.`;
    }

    if (successCount > 0) {
      return `Uploaded ${successCount} PDF${successCount === 1 ? '' : 's'}. ${errors.length} failed. ${formatUploadErrors(errors)}`.trim();
    }

    return formatUploadErrors(errors) || 'Failed to upload files.';
  }

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e.target.files?.length) return;
    await uploadSelectedPdfFiles(e.target.files);
  }

  async function uploadSelectedPdfFiles(fileList: FileList | File[]) {
    if (!id) return;
    if (uploadingCount > 0) {
      setSaveMessage('Please wait for the current PDF upload batch to finish.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const { validFiles, invalidFileNames, oversizedFileNames } = getPdfUploadSelection(fileList, MAX_KNOWLEDGE_BASE_PDF_SIZE_BYTES);
    const errors = invalidFileNames.map((fileName) => `${fileName}: Not a PDF file.`);
    errors.push(...oversizedFileNames.map((fileName) => `${fileName}: File too large. Maximum size is ${MAX_KNOWLEDGE_BASE_PDF_SIZE_MB}MB.`));

    if (validFiles.length === 0) {
      setSaveMessage(errors.length > 0 ? formatUploadErrors(errors) : 'Please upload PDF files only.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    let successCount = 0;
    setUploadingCount(validFiles.length);
    setSaveMessage('');

    try {
      for (const file of validFiles) {
        try {
          const result = await uploadPDF(id, file);
          if (result.success && result.data) {
            successCount += 1;
            setFiles((currentFiles) => [result.data as FileData, ...currentFiles]);
          } else {
            errors.push(`${file.name}: ${result.error || 'Failed to upload file.'}`);
          }
        } catch (e) {
          errors.push(`${file.name}: ${e instanceof Error ? e.message : 'Failed to upload file.'}`);
        } finally {
          setUploadingCount((currentCount) => Math.max(currentCount - 1, 0));
        }
      }
    } finally {
      setUploadingCount(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }

    setSaveMessage(buildUploadSummary(successCount, errors));
  }

  function handleDragOver(e: React.DragEvent) { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }
  function handleDragLeave(e: React.DragEvent) { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }
  async function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (!e.dataTransfer.files?.length) return;
    await uploadSelectedPdfFiles(e.dataTransfer.files);
  }

  async function handleDeleteFile(fileId: string, filename: string) {
    if (!await confirm({ title: 'Delete File', description: `Are you sure you want to delete "${filename}"?`, confirmLabel: 'Delete', destructive: true })) return;
    try { const result = await deleteFile(id!, fileId); if (result.success) setFiles(files.filter((f) => f.id !== fileId)); }
    catch (e) { toast.error('Failed to delete file'); }
  }

  function handlePresetChange(presetKey: ThemePresetKey | 'custom') {
    setSelectedPreset(presetKey);
    if (presetKey !== 'custom') {
      const preset = THEME_PRESETS[presetKey];
      setTheme((prev) => ({
        ...prev,
        primaryColor: preset.primaryColor,
        headerBgColor: preset.headerBgColor,
        headerTextColor: preset.headerTextColor,
        chatBgColor: preset.chatBgColor,
        userBubbleColor: preset.userBubbleColor,
        userTextColor: preset.userTextColor,
        assistantBubbleColor: preset.assistantBubbleColor,
        assistantTextColor: preset.assistantTextColor,
      }));
    }
  }

  function handleThemeColorChange(key: ThemeColorKey, value: string) { setTheme((prev) => ({ ...prev, [key]: value })); setSelectedPreset('custom'); }
  function handleThemeOptionChange<K extends keyof Theme>(key: K, value: Theme[K]) { setTheme((prev) => ({ ...prev, [key]: value })); }

  async function handleAppearanceSave(e: React.FormEvent) {
    e.preventDefault();
    if (logoUrl.trim()) {
      const trimmedUrl = logoUrl.trim();
      if (trimmedUrl.startsWith('file://') || trimmedUrl.startsWith('file:')) {
        setAppearanceToast({ message: 'Logo URL must be accessible via HTTP/HTTPS.', type: 'error' });
        setTimeout(() => setAppearanceToast(null), 5000); return;
      }
      if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
        setAppearanceToast({ message: 'Logo URL must start with http:// or https://', type: 'error' });
        setTimeout(() => setAppearanceToast(null), 5000); return;
      }
    }
    setAppearanceSaving(true); setAppearanceMessage('');
    try {
      await updateChatbot(id!, { theme_json: JSON.stringify(theme), logo_url: logoUrl.trim() || null });
      setAppearanceMessage('Saved successfully!');
      setTimeout(() => setAppearanceMessage(''), 3000);
      setSavedValues(prev => ({ ...prev, logoUrl: logoUrl || '', theme: JSON.stringify(theme) }));
      appearanceDirty.clearDraft();
      setWidgetKey(k => k + 1);
    } catch (e) { setAppearanceMessage('Failed to save'); }
    finally { setAppearanceSaving(false); }
  }

  function renderSegmentedControl<T extends string>(
    options: Array<{ value: T; label: string }>,
    selected: T,
    onChange: (value: T) => void
  ) {
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {options.map((option) => {
          const active = option.value === selected;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}`,
                background: active ? 'var(--color-primary-light)' : 'var(--color-bg-white)',
                color: active ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: '13px',
              }}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    );
  }

  async function handleAddFaq(e: React.FormEvent) {
    e.preventDefault();
    if (!newFaqQuestion.trim() || !newFaqAnswer.trim()) return;
    setFaqSaving(true);
    try {
      const result = await createFAQ(id!, { question: newFaqQuestion.trim(), answer: newFaqAnswer.trim(), sort_order: faqs.length });
      if (result.success && result.data) { setFaqs([...faqs, result.data]); setNewFaqQuestion(''); setNewFaqAnswer(''); }
    } catch (e) { toast.error('Failed to add FAQ'); }
    finally { setFaqSaving(false); }
  }

  async function handleUpdateFaq(faq: FAQ) {
    setFaqSaving(true);
    try {
      const result = await updateFAQ(id!, faq.id, { question: faq.question, answer: faq.answer });
      if (result.success && result.data) { setFaqs(faqs.map((f) => (f.id === faq.id ? result.data! : f))); setEditingFaq(null); }
    } catch (e) { toast.error('Failed to update FAQ'); }
    finally { setFaqSaving(false); }
  }

  async function handleDeleteFaq(faqId: string) {
    if (!await confirm({ title: 'Delete FAQ', description: 'Are you sure you want to delete this FAQ?', confirmLabel: 'Delete', destructive: true })) return;
    try { await deleteFAQ(id!, faqId); setFaqs(faqs.filter((f) => f.id !== faqId)); } catch (e) { toast.error('Failed to delete FAQ'); }
  }

  // ========== Email Responder Tab handlers ==========
  async function handleConnectEmail(provider: 'gmail' | 'outlook' | 'forwarding') {
    if (isAdmin && !await confirm({
      title: 'Connect Email Account',
      description: 'You are connecting an email account on behalf of the customer.\n\nThis will replace any existing email connection for this chatbot.\n\nAre you sure you want to proceed?',
      confirmLabel: 'Connect',
    })) return;
    setConnectingProvider(provider);
    setEmailDiagnostics(null);
    setEmailDiagnosticsError('');
    setEmailDiagnosticTest(null);
    setEmailDiagnosticTestCheck(null);
    setEmailDiagnosticTestError('');
    setEmailDraftTest(null);
    setEmailDraftTestError('');
    try {
      const result = await connectEmail(id!, provider);
      if (result.success && result.data) {
        if (result.data.authorization_url) {
          // OAuth flow — redirect user to the provider's consent screen
          window.location.href = result.data.authorization_url;
          return; // Don't clear connectingProvider — page is navigating away
        }
        // Forwarding flow — show the connection immediately
        setEmailConnection({
          connected: true,
          provider,
          email_address: null,
          forwarding_address: result.data.forwarding_address || null,
          status: (result.data.status as any) || 'active',
        });
      }
    } catch (e) { toast.error('Failed to connect email'); }
    finally { setConnectingProvider(null); }
  }

  async function handleDisconnectEmail() {
    const description = isAdmin
      ? 'WARNING: You are disconnecting the customer\'s email account.\n\nThis will stop all AI email draft generation for this chatbot. The customer will need to reconnect their email account to resume.\n\nAre you sure you want to proceed?'
      : 'Are you sure you want to disconnect the email account?';
    if (!await confirm({ title: 'Disconnect Email', description, confirmLabel: 'Disconnect', destructive: true })) return;
    try {
      const result = await disconnectEmail(id!);
      if (result.success) {
        setEmailConnection(null);
        setEmailDiagnostics(null);
        setEmailDiagnosticsError('');
        setEmailDiagnosticTest(null);
        setEmailDiagnosticTestCheck(null);
        setEmailDiagnosticTestError('');
        setEmailDraftTest(null);
        setEmailDraftTestError('');
      }
    }
    catch (e) { toast.error('Failed to disconnect email'); }
  }

  async function handleRunEmailDiagnostics() {
    if (!id) return;
    setEmailDiagnosticsLoading(true);
    setEmailDiagnosticsError('');
    try {
      const result = await getEmailConnectionDiagnostics(id);
      if (result.success && result.data) {
        setEmailDiagnostics(result.data);
      } else {
        setEmailDiagnosticsError(result.error || 'Failed to run diagnostics');
      }
    } catch {
      setEmailDiagnosticsError('Failed to run diagnostics');
    } finally {
      setEmailDiagnosticsLoading(false);
    }
  }

  async function handleCreateEmailDiagnosticTest() {
    if (!id) return;
    setEmailDiagnosticTestLoading(true);
    setEmailDiagnosticTestError('');
    setEmailDiagnosticTestCheck(null);
    try {
      const result = await createEmailDiagnosticTest(id);
      if (result.success && result.data) {
        setEmailDiagnosticTest(result.data);
      } else {
        setEmailDiagnosticTestError(result.error || 'Failed to send diagnostic test email');
      }
    } catch {
      setEmailDiagnosticTestError('Failed to send diagnostic test email');
    } finally {
      setEmailDiagnosticTestLoading(false);
    }
  }

  async function handleTestEmailDraftCreation() {
    if (!id) return;
    setEmailDraftTestLoading(true);
    setEmailDraftTestError('');
    setEmailDraftTest(null);
    try {
      const result = await testEmailDraftCreation(id);
      if (result.success && result.data) {
        setEmailDraftTest(result.data);
      } else {
        setEmailDraftTestError([result.error || 'Failed to create and verify draft', result.detail].filter(Boolean).join(' '));
      }
    } catch {
      setEmailDraftTestError('Failed to create and verify draft');
    } finally {
      setEmailDraftTestLoading(false);
    }
  }

  async function handleCheckEmailDiagnosticTest() {
    if (!id || !emailDiagnosticTest) return;
    setEmailDiagnosticTestChecking(true);
    setEmailDiagnosticTestError('');
    try {
      const result = await checkEmailDiagnosticTest(id, emailDiagnosticTest.id);
      if (result.success && result.data) {
        setEmailDiagnosticTestCheck(result.data);
      } else {
        setEmailDiagnosticTestError(result.error || 'Failed to check diagnostic test email');
      }
    } catch {
      setEmailDiagnosticTestError('Failed to check diagnostic test email');
    } finally {
      setEmailDiagnosticTestChecking(false);
    }
  }

  async function handleSaveEmailConfig(e: React.FormEvent) {
    e.preventDefault();
    if (!emailConfig) return;
    setEmailConfigSaving(true); setEmailConfigMessage('');
    try {
      const result = await updateEmailResponderConfig(id!, {
        enabled: emailConfig.enabled, response_tone: emailConfig.response_tone,
        email_signature: emailConfig.email_signature, scan_interval_minutes: emailConfig.scan_interval_minutes,
        cc_forwarder_on_forward: Boolean(emailConfig.cc_forwarder_on_forward),
        exclude_newsletters: emailConfig.exclude_newsletters ?? true,
      });
      if (result.success && result.data) {
        setEmailConfig(result.data);
        setSavedEmailConfig({
          response_tone: result.data.response_tone || '',
          email_signature: result.data.email_signature || '',
          scan_interval_minutes: String(result.data.scan_interval_minutes ?? ''),
          cc_forwarder_on_forward: String(Boolean(result.data.cc_forwarder_on_forward)),
          exclude_newsletters: String(result.data.exclude_newsletters ?? true),
        });
        emailConfigDirty.clearDraft();
      }
      setEmailConfigMessage('Saved successfully!');
      setTimeout(() => setEmailConfigMessage(''), 3000);
    } catch (e) { setEmailConfigMessage('Failed to save'); }
    finally { setEmailConfigSaving(false); }
  }

  async function handleAddTrainingEmail(e: React.FormEvent) {
    e.preventDefault();
    if (!newTrainingIncoming.trim() || !newTrainingResponse.trim()) return;
    setTrainingSaving(true);
    try {
      const result = await addTrainingEmail(id!, { subject: newTrainingSubject, incoming_body: newTrainingIncoming, response_body: newTrainingResponse });
      if (result.success && result.data) {
        setTrainingEmails([result.data, ...trainingEmails]);
        setNewTrainingSubject(''); setNewTrainingIncoming(''); setNewTrainingResponse('');
        setShowAddTraining(false);
      }
    } catch (e) { toast.error('Failed to add training email'); }
    finally { setTrainingSaving(false); }
  }

  async function handleDeleteTrainingEmail(emailId: string) {
    if (!await confirm({ title: 'Delete Training Email', description: 'Are you sure you want to delete this training email?', confirmLabel: 'Delete', destructive: true })) return;
    try { await deleteTrainingEmail(id!, emailId); setTrainingEmails(trainingEmails.filter(e => e.id !== emailId)); }
    catch (e) { toast.error('Failed to delete training email'); }
  }

  async function handleEmlUpload(side: 'incoming' | 'response', file: File) {
    setUploadingTraining(true);
    setUploadResult(null);
    try {
      const files = side === 'incoming' ? { incoming: file } : { response: file };
      const result = await uploadTrainingEmailFiles(id!, files);
      if (result.success && result.data) {
        if (result.data.created.length > 0) {
          setTrainingEmails([...result.data.created, ...trainingEmails]);
          setUploadResult({ created: result.data.created.length, errors: result.data.errors.map(e => e.error) });
          setShowAddTraining(false);
        } else if (result.data.parsed) {
          // Pre-fill the form
          setNewTrainingSubject(result.data.parsed.subject || '');
          if (result.data.parsed.incoming_body) setNewTrainingIncoming(result.data.parsed.incoming_body);
          if (result.data.parsed.response_body) setNewTrainingResponse(result.data.parsed.response_body);
          setShowAddTraining(true);
        }
      }
    } catch { toast.error('Failed to process EML file'); }
    finally {
      setUploadingTraining(false);
      if (incomingFileRef.current) incomingFileRef.current.value = '';
      if (responseFileRef.current) responseFileRef.current.value = '';
    }
  }

  async function uploadTrainingPdfFiles(fileList: FileList | File[]) {
    if (!id) return;
    if (uploadingTraining) {
      setUploadResult({ created: 0, errors: ['Please wait for the current training PDF upload to finish.'] });
      return;
    }

    const { validFiles, invalidFileNames, oversizedFileNames } = getPdfUploadSelection(fileList, MAX_TRAINING_PDF_SIZE_BYTES);
    const errors = invalidFileNames.map((fileName) => `${fileName}: Not a PDF file.`);
    errors.push(...oversizedFileNames.map((fileName) => `${fileName}: File too large. Maximum size is ${MAX_TRAINING_PDF_SIZE_MB}MB.`));

    if (validFiles.length === 0) {
      setUploadResult({ created: 0, errors: errors.length > 0 ? errors : ['Please upload PDF files only.'] });
      if (pdfFileRef.current) pdfFileRef.current.value = '';
      return;
    }

    setUploadingTraining(true);
    setTrainingPdfUploadCount(validFiles.length);
    setUploadResult(null);

    try {
      const result = await uploadTrainingEmailFiles(id, { pdf: validFiles });
      if (result.success && result.data) {
        const createdTrainingEmails = result.data.created;
        if (createdTrainingEmails.length > 0) {
          setTrainingEmails((currentEmails) => [...createdTrainingEmails, ...currentEmails]);
        }
        setUploadResult({
          created: createdTrainingEmails.length,
          errors: [...errors, ...result.data.errors.map(e => e.error)],
        });
      } else {
        setUploadResult({ created: 0, errors: [...errors, result.error || 'Failed to process PDF files'] });
      }
    } catch { toast.error('Failed to process PDF files'); }
    finally {
      setUploadingTraining(false);
      setTrainingPdfUploadCount(0);
      if (pdfFileRef.current) pdfFileRef.current.value = '';
    }
  }

  async function handleTrainingPdfInput(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e.target.files?.length) return;
    await uploadTrainingPdfFiles(e.target.files);
  }

  function handleTrainingPdfDragOver(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!uploadingTraining) setIsTrainingPdfDragging(true);
  }

  function handleTrainingPdfDragLeave(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    setIsTrainingPdfDragging(false);
  }

  async function handleTrainingPdfDrop(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    setIsTrainingPdfDragging(false);
    if (!e.dataTransfer.files?.length) return;
    await uploadTrainingPdfFiles(e.dataTransfer.files);
  }

  function getEmbedSnippet() {
    const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const workerUrl = isDev ? 'http://localhost:8787' : import.meta.env.VITE_WORKER_URL;
    const widgetUrl = isDev ? 'http://localhost:5174' : import.meta.env.VITE_WIDGET_URL;
    if (!isDev && (!workerUrl || !widgetUrl)) return `<!-- Error: VITE_WORKER_URL and VITE_WIDGET_URL must be configured -->`;
    return `<script\n  src="${widgetUrl}/chat-widget.js"\n  data-chatbot-id="${id}"\n  data-api-url="${workerUrl}"\n  async\n></script>`;
  }

  function copyToClipboard(text: string) { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }

  function formatDiagnosticDate(value?: string | null) {
    if (!value) return 'Never';
    const normalizedValue = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)
      ? `${value.replace(' ', 'T')}Z`
      : value;
    const date = new Date(normalizedValue);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
  }

  function diagnosticHealthLabel(health?: EmailConnectionDiagnostics['health']) {
    switch (health) {
      case 'connected_with_unread_mail': return 'Connected, unread mail visible';
      case 'connected_no_unread_mail': return 'Connected, no unread Inbox mail';
      case 'reconnect_required': return 'Reconnect required';
      case 'provider_error': return 'Diagnostic check failed';
      case 'not_connected': return 'Not connected';
      default: return 'Connected';
    }
  }

  function formatDiagnosticLabel(value: string) {
    return value
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  function renderDiagnosticSamples(samples: NonNullable<NonNullable<EmailConnectionDiagnostics['provider_diagnostics']>['unreadSamples']>, emptyText: string) {
    if (!samples.length) {
      return <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>{emptyText}</div>;
    }
    return (
      <div style={{ display: 'grid', gap: '8px' }}>
        {samples.slice(0, 5).map((sample, index) => (
          <div key={`${sample.receivedAt || index}-${sample.senderDomain || ''}`} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '8px 0', borderTop: index === 0 ? 'none' : '1px solid var(--color-border-light)' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: 600 }}>{sample.subject || sample.senderDomain || sample.sender || 'Message'}</div>
              <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                {sample.sender || sample.senderDomain || 'Sender hidden'}{sample.isRead !== undefined ? ` - ${sample.isRead ? 'Read' : 'Unread'}` : ''}
              </div>
            </div>
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>{formatDiagnosticDate(sample.receivedAt)}</div>
          </div>
        ))}
      </div>
    );
  }

  const canEdit = user?.role === 'admin' || (user?.role === 'sales' && chatbot?.editable_by_sales) || isCustomer;
  const previewLogoSize = PREVIEW_LOGO_SIZES[theme.headerSize][theme.logoSize];
  const previewLogoFrameSize = theme.logoBackground === 'none' ? previewLogoSize : previewLogoSize + 10;
  const previewLogoRadius = theme.logoShape === 'circle' ? '50%' : theme.logoShape === 'rounded' ? '10px' : '4px';
  const previewLogoBackground = theme.logoBackground === 'solid' ? '#ffffff' : theme.logoBackground === 'subtle' ? 'rgba(255,255,255,0.18)' : 'transparent';
  const previewPanelWidth = theme.panelSize === 'large' ? 380 : theme.panelSize === 'compact' ? 300 : theme.panelSize === 'legacy' ? 320 : 340;
  const previewWideLogo = Boolean(logoUrl && theme.logoLayout === 'wide');
  const previewWideLogoHeight = theme.headerSize === 'showcase' ? 58 : theme.headerSize === 'comfortable' ? 44 : theme.headerSize === 'compact' ? 34 : 32;
  const previewWideLogoJustify = theme.wideLogoAlign === 'left' ? 'flex-start' : 'center';
  const previewWideLogoObjectPosition = theme.wideLogoAlign === 'left' ? 'left center' : 'center';
  const previewWideLogoFrameStyle: CSSProperties = theme.wideLogoAlign === 'left'
    ? { flex: '1 1 auto', minWidth: 0, height: `${previewWideLogoHeight}px`, display: 'flex', alignItems: 'center', justifyContent: previewWideLogoJustify, overflow: 'hidden' }
    : { position: 'absolute', inset: 0, padding: '10px 72px', height: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', pointerEvents: 'none' };

  if (loading) {
    return (
      <div>
        <header className="header">
          <div className="flex flex-center gap-3">
            <div className="skeleton" style={{ width: 80, height: 20 }} />
            <span className="breadcrumb-separator">/</span>
            <div className="skeleton" style={{ width: 120, height: 24 }} />
          </div>
          <div className="header-user"><div className="skeleton" style={{ width: 150, height: 16 }} /></div>
        </header>
        <div className="container">
          <div className="tabs">
            {['Chatbot', 'Knowledge Base', 'Email Responder'].map((tab) => (<div key={tab} className="tab">{tab}</div>))}
          </div>
          <div className="card">
            <div className="skeleton mb-4" style={{ height: 20, width: '30%' }} />
            <div className="skeleton mb-3" style={{ height: 40 }} />
            <div className="skeleton mb-4" style={{ height: 20, width: '25%' }} />
            <div className="skeleton mb-3" style={{ height: 100 }} />
            <div className="skeleton" style={{ height: 40, width: 100 }} />
          </div>
        </div>
      </div>
    );
  }

  if (!chatbot) {
    return (
      <div className="container">
        <div className="card empty-state">
          <div className="empty-state-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <h3 className="empty-state-title">Chatbot not found</h3>
          <p className="empty-state-description">This chatbot may have been deleted or doesn't exist.</p>
          {!isCustomer && <Link to="/" className="btn btn-primary">Back to Dashboard</Link>}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Toast notifications */}
      {appearanceToast && (
        <div style={{ position: 'fixed', top: '24px', right: '24px', zIndex: 9999, padding: '16px 20px', borderRadius: '8px', background: appearanceToast.type === 'warning' ? 'var(--color-warning)' : 'var(--color-danger)', color: 'white', fontSize: '15px', fontWeight: 600, boxShadow: '0 10px 25px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', gap: '12px', animation: 'slideIn 0.3s ease-out', maxWidth: '400px' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
          <span>{appearanceToast.message}</span>
        </div>
      )}
      {domainToast && (
        <div style={{ position: 'fixed', top: '24px', right: '24px', zIndex: 9999, padding: '16px 20px', borderRadius: '8px', background: domainToast.type === 'saving' ? 'var(--color-primary)' : domainToast.type === 'success' ? 'var(--color-success)' : 'var(--color-danger)', color: 'white', fontSize: '15px', fontWeight: 600, boxShadow: '0 10px 25px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', gap: '12px', animation: 'slideIn 0.3s ease-out' }}>
          {domainToast.type === 'saving' && <span className="spinner spinner-sm" style={{ borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} />}
          {domainToast.type === 'success' && <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>}
          {domainToast.type === 'error' && <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>}
          <span>{domainToast.message}</span>
        </div>
      )}

      {/* Header */}
      <AppHeader actions={<HeaderUtilityActions />}>
        <div className="flex flex-center gap-3">
          {!isCustomer && (
            <>
              <Link to="/" className="nav-link nav-link-brand">
                <img src="/logo.png" alt="Neural Intelligence" className="nav-logo" />
                Dashboard
              </Link>
              <span className="breadcrumb-separator">/</span>
            </>
          )}
          {isCustomer && (
            <span className="nav-link nav-link-brand" style={{ cursor: 'default' }}>
              <img src="/logo.png" alt="Neural Intelligence" className="nav-logo" />
            </span>
          )}
          <div>
            <h1>{chatbot.name}</h1>
            {isCustomer && <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)', fontWeight: 400 }}>Your AI Assistant</div>}
          </div>
        </div>
      </AppHeader>

      <div className="container">
        {/* Analytics */}
        <div className={`analytics-panel${(chatbot?.analytics_enabled || showAnalyticsPreview) && stats ? ' open' : ''}`}>
          {stats && (
            <div className="analytics-layout" style={{ marginBottom: '24px' }}>
              {/* Stat cards */}
              <div className="analytics-cards">
                {[
                  { label: 'Chats (24h)', value: stats.last24h, color: 'var(--color-primary)' },
                  { label: 'Chats (7d)', value: stats.last7d, color: 'var(--color-primary)' },
                  { label: 'Chats (30d)', value: stats.last30d, color: 'var(--color-primary)' },
                  { label: 'Total chats', value: stats.total, color: 'var(--color-primary)' },
                  ...(messageStats ? [{ label: 'Avg msgs/chat', value: messageStats.avg_messages_per_conversation, color: 'var(--color-primary)' }] : []),
                  ...(emailDraftStats && emailDraftStats.total > 0 ? [
                    { label: 'Drafts (24h)', value: emailDraftStats.last24h, color: 'var(--color-teal)' },
                    { label: 'Drafts (7d)', value: emailDraftStats.last7d, color: 'var(--color-teal)' },
                    { label: 'Drafts (30d)', value: emailDraftStats.last30d, color: 'var(--color-teal)' },
                    { label: 'Total drafts', value: emailDraftStats.total, color: 'var(--color-teal)' },
                    ...(emailDraftStats.errors > 0 ? [{ label: 'Errors', value: emailDraftStats.errors, color: 'var(--color-teal)', danger: true }] : []),
                  ] : []),
                ].map(({ label, value, color, danger }: { label: string; value: number; color: string; danger?: boolean }) => (
                  <div key={label} style={{ background: 'var(--color-bg-white)', border: '1px solid var(--color-border)', borderLeft: `3px solid ${color}`, borderRadius: 'var(--radius-md)', padding: '12px' }}>
                    <div style={{ fontSize: '11px', color: 'var(--color-text-muted)', fontWeight: 500, marginBottom: '2px' }}>{label}</div>
                    <div style={{ fontSize: '22px', fontWeight: 700, color: danger && value > 0 ? 'var(--color-danger)' : 'var(--color-text-primary)' }}>{value}</div>
                  </div>
                ))}
              </div>
              {/* Chart */}
              {dailyStats.length > 0 ? (
                <div className="analytics-chart" style={{ background: 'var(--color-bg-white)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: '20px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--color-text-primary)', margin: '0 0 4px' }}>Activity (Last 30 Days)</h3>
                  <BarChart
                    labels={dailyStats.map(d => new Date(d.day + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' }))}
                    series={[
                      { name: 'Conversations', color: 'var(--color-primary)', data: dailyStats.map(d => d.conversations) },
                      ...(dailyStats.some(d => d.drafts > 0) ? [{ name: 'Email drafts', color: 'var(--color-teal)', data: dailyStats.map(d => d.drafts) }] : []),
                    ]}
                    height={200}
                  />
                </div>
              ) : (
                <div className="analytics-chart" style={{ background: 'var(--color-bg-white)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted)', fontSize: '14px' }}>
                  No activity data yet
                </div>
              )}
            </div>
          )}
        </div>
        {/* ===== Top-Level Tab Navigation ===== */}
        <div style={{ marginBottom: '32px', borderBottom: '2px solid var(--color-border)', paddingBottom: '0' }}>
          <div className="detail-tabs">
            {canEdit && visibleTabs.map(tab => (
              <button
                key={tab}
                className={`tab ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
                style={{ fontSize: '15px', padding: '12px 18px', fontWeight: 600, border: 'none', background: 'transparent', borderBottom: activeTab === tab ? '3px solid var(--color-primary)' : '3px solid transparent', borderRadius: '0', color: activeTab === tab ? 'var(--color-primary)' : 'var(--color-text-secondary)', whiteSpace: 'nowrap', transition: 'all 0.2s ease' }}
              >
                {tabLabels[tab]}
                {tabIsDirty(tab) && <span className="dirty-dot" />}
              </button>
            ))}
          </div>
        </div>

        {isCustomer && chatbot && (
          <SetupProgress
            chatbotId={chatbot.id}
            completion={{
              hasInstructions: instructions.trim().length > 50,
              hasKnowledge: files.length > 0 || faqs.length > 0,
              hasDomain: chatbot.domains.length > 0,
              emailConnected: Boolean(emailConnection?.connected),
              hasTrainingEmails: trainingEmails.length > 0,
            }}
            emailTabVisible={visibleTabs.includes('email-responder')}
            onNavigateTab={setActiveTab}
          />
        )}

        {/* ===== CONFIG TAB (admin only) ===== */}
        {activeTab === 'config' && (
            <div className="card">
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '24px' }}>Access & Permissions</h3>

              {/* Sales Team Access Toggle */}
              <div style={{ padding: '20px', background: 'linear-gradient(135deg, var(--color-bg-secondary) 0%, var(--color-bg-secondary) 100%)', border: '2px solid var(--color-border)', borderRadius: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                  <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: chatbot?.editable_by_sales ? 'var(--color-success-light)' : 'var(--color-bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={chatbot?.editable_by_sales ? 'var(--color-badge-success-text)' : 'var(--color-text-secondary)'} strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '17px', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: '6px' }}>Sales Team Access</h3>
                    <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '16px' }}>Grant sales users permission to edit this chatbot.</p>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: 'var(--color-bg-white)', border: `2px solid ${chatbot?.editable_by_sales ? 'var(--color-success)' : 'var(--color-border)'}`, borderRadius: '8px', cursor: 'pointer', fontSize: '15px', fontWeight: 500 }}>
                      <div style={{ position: 'relative', width: '48px', height: '24px', flexShrink: 0 }}>
                        <input type="checkbox" checked={Boolean(chatbot?.editable_by_sales)} disabled={toggleLoading} onChange={async (e) => {
                          const newValue = e.target.checked; setToggleLoading(true);
                          try {
                            const res = await updateChatbot(id!, { editable_by_sales: newValue });
                            if (res.success) { setChatbot(prev => prev ? { ...prev, editable_by_sales: newValue } : prev); }
                          } catch {} finally { setToggleLoading(false); }
                        }} style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
                        <div style={{ width: '48px', height: '24px', background: chatbot?.editable_by_sales ? 'var(--color-success)' : 'var(--color-text-muted)', borderRadius: '12px', transition: 'background 0.2s ease', position: 'relative', opacity: toggleLoading ? 0.6 : 1 }}>
                          <div style={{ position: 'absolute', top: '2px', left: chatbot?.editable_by_sales ? '26px' : '2px', width: '20px', height: '20px', background: 'white', borderRadius: '50%', transition: 'left 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} />
                        </div>
                      </div>
                      <span>{chatbot?.editable_by_sales ? 'Sales can edit this chatbot' : 'Sales cannot edit (view only)'}</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Testing Mode Toggle */}
              <div style={{ marginTop: '20px', padding: '20px', background: 'linear-gradient(135deg, var(--color-bg-secondary) 0%, var(--color-bg-secondary) 100%)', border: '2px solid var(--color-border)', borderRadius: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                  <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: chatbot?.is_testing ? 'var(--color-warning-light)' : 'var(--color-bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={chatbot?.is_testing ? 'var(--color-badge-warning-text)' : 'var(--color-text-secondary)'} strokeWidth="2"><path d="M9 3h6v2H9z" /><path d="M8 5l-1 8h10l-1-8" /><path d="M7 13l-2 8h14l-2-8" /><circle cx="10" cy="10" r="1" /><circle cx="14" cy="17" r="1" /></svg>
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '17px', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: '6px' }}>Testing Mode</h3>
                    <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '16px' }}>Mark this chatbot as a test. It will appear under the "Testing" category on the chatbot list.</p>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: 'var(--color-bg-white)', border: `2px solid ${chatbot?.is_testing ? 'var(--color-warning)' : 'var(--color-border)'}`, borderRadius: '8px', cursor: 'pointer', fontSize: '15px', fontWeight: 500 }}>
                      <div style={{ position: 'relative', width: '48px', height: '24px', flexShrink: 0 }}>
                        <input type="checkbox" checked={Boolean(chatbot?.is_testing)} disabled={toggleLoading} onChange={async (e) => {
                          const newValue = e.target.checked; setToggleLoading(true);
                          try {
                            const res = await updateChatbot(id!, { is_testing: newValue });
                            if (res.success) { setChatbot(prev => prev ? { ...prev, is_testing: newValue } : prev); }
                          } catch {} finally { setToggleLoading(false); }
                        }} style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
                        <div style={{ width: '48px', height: '24px', background: chatbot?.is_testing ? 'var(--color-warning)' : 'var(--color-text-muted)', borderRadius: '12px', transition: 'background 0.2s ease', position: 'relative', opacity: toggleLoading ? 0.6 : 1 }}>
                          <div style={{ position: 'absolute', top: '2px', left: chatbot?.is_testing ? '26px' : '2px', width: '20px', height: '20px', background: 'white', borderRadius: '50%', transition: 'left 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} />
                        </div>
                      </div>
                      <span>{chatbot?.is_testing ? 'Testing mode active' : 'Not in testing'}</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Analytics Toggle */}
              <div style={{ marginTop: '20px', padding: '20px', background: 'linear-gradient(135deg, var(--color-bg-secondary) 0%, var(--color-bg-secondary) 100%)', border: '2px solid var(--color-border)', borderRadius: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                  <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: chatbot?.analytics_enabled ? 'var(--color-primary-light)' : 'var(--color-bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={chatbot?.analytics_enabled ? 'var(--color-primary)' : 'var(--color-text-secondary)'} strokeWidth="2"><line x1="12" y1="20" x2="12" y2="10" /><line x1="18" y1="20" x2="18" y2="4" /><line x1="6" y1="20" x2="6" y2="16" /></svg>
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '17px', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: '6px' }}>Analytics</h3>
                    <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '16px' }}>Show conversation statistics and usage charts on this chatbot's page. When enabled, sales users and customers will also see analytics.</p>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: 'var(--color-bg-white)', border: `2px solid ${chatbot?.analytics_enabled ? 'var(--color-success)' : 'var(--color-border)'}`, borderRadius: '8px', cursor: 'pointer', fontSize: '15px', fontWeight: 500 }}>
                      <div style={{ position: 'relative', width: '48px', height: '24px', flexShrink: 0 }}>
                        <input type="checkbox" checked={Boolean(chatbot?.analytics_enabled)} disabled={toggleLoading} onChange={async (e) => {
                          const newValue = e.target.checked; setToggleLoading(true);
                          try {
                            const res = await updateChatbot(id!, { analytics_enabled: newValue });
                            if (res.success) {
                              setChatbot(prev => prev ? { ...prev, analytics_enabled: newValue } : prev);
                              if (newValue && !stats) fetchAnalytics();
                            }
                          } catch {} finally { setToggleLoading(false); }
                        }} style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
                        <div style={{ width: '48px', height: '24px', background: chatbot?.analytics_enabled ? 'var(--color-success)' : 'var(--color-text-muted)', borderRadius: '12px', transition: 'background 0.2s ease', position: 'relative', opacity: toggleLoading ? 0.6 : 1 }}>
                          <div style={{ position: 'absolute', top: '2px', left: chatbot?.analytics_enabled ? '26px' : '2px', width: '20px', height: '20px', background: 'white', borderRadius: '50%', transition: 'left 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} />
                        </div>
                      </div>
                      <span>{chatbot?.analytics_enabled ? 'Analytics enabled' : 'Analytics disabled'}</span>
                    </label>
                    {!chatbot?.analytics_enabled && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px' }}>
                        <button
                          className={showAnalyticsPreview ? 'btn btn-ghost' : 'btn btn-primary'}
                          onClick={async () => {
                            if (showAnalyticsPreview) {
                              setShowAnalyticsPreview(false);
                            } else {
                              if (!stats) await fetchAnalytics();
                              setShowAnalyticsPreview(true);
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="20" x2="12" y2="10" /><line x1="18" y1="20" x2="18" y2="4" /><line x1="6" y1="20" x2="6" y2="16" /></svg>
                          {showAnalyticsPreview ? 'Hide Analytics' : 'View Analytics'}
                        </button>
                        <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>Admin only — not visible to other users</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Customer Access Section */}
              <div style={{ marginTop: '20px', padding: '20px', background: 'linear-gradient(135deg, var(--color-bg-secondary) 0%, var(--color-bg-secondary) 100%)', border: '2px solid var(--color-border)', borderRadius: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                  <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: 'var(--color-primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><path d="M20 8v6" /><path d="M23 11h-6" /></svg>
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '17px', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: '6px' }}>Customer Access</h3>
                    <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '16px' }}>Control which features customers can access and manage customer accounts.</p>

                    {/* Feature Visibility Toggles */}
                    <div style={{ marginBottom: '20px' }}>
                      <h4 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: '12px' }}>Feature Visibility</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {/* Chatbot toggle */}
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: 'var(--color-bg-white)', border: `2px solid ${chatbot?.customer_chatbot_enabled !== false ? 'var(--color-success)' : 'var(--color-border)'}`, borderRadius: '8px', cursor: (chatbot?.customer_chatbot_enabled !== false && !chatbot?.customer_email_responder_enabled) ? 'not-allowed' : 'pointer', fontSize: '15px', fontWeight: 500, opacity: (chatbot?.customer_chatbot_enabled !== false && !chatbot?.customer_email_responder_enabled) ? 0.7 : 1 }}>
                          <div style={{ position: 'relative', width: '48px', height: '24px', flexShrink: 0 }}>
                            <input type="checkbox" checked={chatbot?.customer_chatbot_enabled !== false} disabled={toggleLoading || (chatbot?.customer_chatbot_enabled !== false && !chatbot?.customer_email_responder_enabled)} onChange={async (e) => {
                              const newValue = e.target.checked; setToggleLoading(true);
                              try {
                                const res = await updateChatbot(id!, { customer_chatbot_enabled: newValue });
                                if (res.success) { setChatbot(prev => prev ? { ...prev, customer_chatbot_enabled: newValue } : prev); }
                              } catch {} finally { setToggleLoading(false); }
                            }} style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
                            <div style={{ width: '48px', height: '24px', background: chatbot?.customer_chatbot_enabled !== false ? 'var(--color-success)' : 'var(--color-text-muted)', borderRadius: '12px', transition: 'background 0.2s ease', position: 'relative', opacity: toggleLoading ? 0.6 : 1 }}>
                              <div style={{ position: 'absolute', top: '2px', left: chatbot?.customer_chatbot_enabled !== false ? '26px' : '2px', width: '20px', height: '20px', background: 'white', borderRadius: '50%', transition: 'left 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} />
                            </div>
                          </div>
                          <div>
                            <span>Chatbot</span>
                            <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginTop: '2px', fontWeight: 400 }}>Allow customers to view and configure the chatbot widget</p>
                          </div>
                        </label>

                        {/* Email Responder toggle */}
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: 'var(--color-bg-white)', border: `2px solid ${chatbot?.customer_email_responder_enabled ? 'var(--color-success)' : 'var(--color-border)'}`, borderRadius: '8px', cursor: (chatbot?.customer_email_responder_enabled && chatbot?.customer_chatbot_enabled === false) ? 'not-allowed' : 'pointer', fontSize: '15px', fontWeight: 500, opacity: (chatbot?.customer_email_responder_enabled && chatbot?.customer_chatbot_enabled === false) ? 0.7 : 1 }}>
                          <div style={{ position: 'relative', width: '48px', height: '24px', flexShrink: 0 }}>
                            <input type="checkbox" checked={Boolean(chatbot?.customer_email_responder_enabled)} disabled={toggleLoading || (Boolean(chatbot?.customer_email_responder_enabled) && chatbot?.customer_chatbot_enabled === false)} onChange={async (e) => {
                              const newValue = e.target.checked; setToggleLoading(true);
                              try {
                                const res = await updateChatbot(id!, { customer_email_responder_enabled: newValue });
                                if (res.success) { setChatbot(prev => prev ? { ...prev, customer_email_responder_enabled: newValue } : prev); }
                              } catch {} finally { setToggleLoading(false); }
                            }} style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
                            <div style={{ width: '48px', height: '24px', background: chatbot?.customer_email_responder_enabled ? 'var(--color-success)' : 'var(--color-text-muted)', borderRadius: '12px', transition: 'background 0.2s ease', position: 'relative', opacity: toggleLoading ? 0.6 : 1 }}>
                              <div style={{ position: 'absolute', top: '2px', left: chatbot?.customer_email_responder_enabled ? '26px' : '2px', width: '20px', height: '20px', background: 'white', borderRadius: '50%', transition: 'left 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} />
                            </div>
                          </div>
                          <div>
                            <span>Email Responder</span>
                            <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginTop: '2px', fontWeight: 400 }}>Allow customers to view and configure the email responder</p>
                          </div>
                        </label>
                      </div>
                      <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginTop: '10px', fontStyle: 'italic' }}>Knowledge Base is automatically available when either feature is enabled.</p>
                    </div>

                    {/* Assigned Customers */}
                    <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '16px' }}>
                      <h4 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: '12px' }}>Assigned Customers</h4>
                      {chatbotCustomers.length === 0 && !showCreateCustomer && (
                        <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', marginBottom: '12px' }}>No customer accounts assigned to this chatbot.</p>
                      )}
                      {chatbotCustomers.length > 0 && (
                        <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {chatbotCustomers.map((customer) => (
                            <div key={customer.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--color-bg-white)', border: '1px solid var(--color-border)', borderRadius: '8px' }}>
                              <div>
                                <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--color-text-primary)' }}>{customer.email}</span>
                                <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginLeft: '12px' }}>Added {new Date(customer.created_at).toLocaleDateString()}</span>
                              </div>
                              <button
                                className="btn"
                                style={{ fontSize: '13px', padding: '4px 10px', color: 'var(--color-danger)', background: 'transparent', border: '1px solid var(--color-danger-border)' }}
                                onClick={async () => {
                                  if (!await confirm({ title: 'Remove Customer', description: 'Are you sure you want to remove this customer account? They will no longer be able to log in.', confirmLabel: 'Remove', destructive: true })) return;
                                  try {
                                    const res = await deleteChatbotCustomer(id!, customer.id);
                                    if (res.success) { setChatbotCustomers(prev => prev.filter(c => c.id !== customer.id)); }
                                    else { toast.error(res.error || 'Failed to remove customer'); }
                                  } catch { toast.error('Failed to remove customer'); }
                                }}
                              >Remove</button>
                            </div>
                          ))}
                        </div>
                      )}

                      {showCreateCustomer ? (
                        <div style={{ padding: '14px', background: 'var(--color-bg-white)', border: '1px solid var(--color-border)', borderRadius: '8px' }}>
                          {/* Mode toggle */}
                          <div style={{ display: 'flex', gap: '4px', marginBottom: '14px', background: 'var(--color-bg-secondary)', borderRadius: '6px', padding: '3px' }}>
                            <button type="button" onClick={() => setCustomerInviteMode(true)} style={{ flex: 1, padding: '6px 12px', fontSize: '13px', fontWeight: 600, border: 'none', borderRadius: '4px', cursor: 'pointer', background: customerInviteMode ? 'var(--color-bg-white)' : 'transparent', color: customerInviteMode ? 'var(--color-text-primary)' : 'var(--color-text-secondary)', boxShadow: customerInviteMode ? 'var(--shadow-sm)' : 'none' }}>Send invite email</button>
                            <button type="button" onClick={() => setCustomerInviteMode(false)} style={{ flex: 1, padding: '6px 12px', fontSize: '13px', fontWeight: 600, border: 'none', borderRadius: '4px', cursor: 'pointer', background: !customerInviteMode ? 'var(--color-bg-white)' : 'transparent', color: !customerInviteMode ? 'var(--color-text-primary)' : 'var(--color-text-secondary)', boxShadow: !customerInviteMode ? 'var(--shadow-sm)' : 'none' }}>Set password manually</button>
                          </div>

                          <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '13px', color: 'var(--color-text-primary)' }}>Email</label>
                            <input type="email" className="input" placeholder="customer@example.com" value={newCustomerEmail} onChange={(e) => setNewCustomerEmail(e.target.value)} style={{ width: '100%', fontSize: '14px', padding: '8px 12px' }} />
                          </div>

                          {!customerInviteMode && (
                            <div style={{ marginBottom: '12px' }}>
                              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '13px', color: 'var(--color-text-primary)' }}>Password</label>
                              <input type="password" className="input" placeholder="••••••••••••" value={newCustomerPassword} onChange={(e) => { setNewCustomerPassword(e.target.value); setCustPwdValidation(validatePasswordStrength(e.target.value)); }} style={{ width: '100%', fontSize: '14px', padding: '8px 12px' }} />
                              {newCustomerPassword && (
                                <div style={{ marginTop: '8px', padding: '10px 12px', background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: '6px' }}>
                                  <p style={{ fontSize: '12px', color: 'var(--color-text-secondary)', margin: '0 0 6px 0', fontWeight: 600 }}>Password Requirements</p>
                                  <div className="grid-2col" style={{ gap: '4px', fontSize: '12px' }}>
                                    {([
                                      ['length', '12+ characters'],
                                      ['number', 'One number'],
                                      ['uppercase', 'One uppercase'],
                                      ['special', 'One special (!@#-)'],
                                      ['lowercase', 'One lowercase'],
                                    ] as const).map(([key, label]) => (
                                      <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        {custPwdValidation[key] ? (
                                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                                        ) : (
                                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="3"><circle cx="12" cy="12" r="10" /></svg>
                                        )}
                                        <span style={{ color: custPwdValidation[key] ? 'var(--color-success)' : 'var(--color-text-muted)' }}>{label}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {customerInviteMode && (
                            <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginBottom: '12px' }}>An email will be sent with a link to set their password.</p>
                          )}

                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <button className="btn btn-primary" style={{ fontSize: '13px', padding: '6px 14px' }}
                              disabled={customerSaving || !newCustomerEmail || (!customerInviteMode && !custPwdValid)}
                              onClick={async () => {
                                setCustomerSaving(true); setCustomerMessage('');
                                try {
                                  if (customerInviteMode) {
                                    const res = await inviteUser({ email: newCustomerEmail, role: 'customer', assigned_chatbot_id: id! });
                                    if (res.success && res.data) {
                                      setChatbotCustomers(prev => [{ id: res.data!.id, email: res.data!.email, created_at: new Date().toISOString() }, ...prev]);
                                      setNewCustomerEmail(''); setShowCreateCustomer(false); setCustomerMessage('');
                                    } else { setCustomerMessage(res.error || 'Failed to send invite'); }
                                  } else {
                                    const res = await createCustomer({ email: newCustomerEmail, password: newCustomerPassword, assigned_chatbot_id: id! });
                                    if (res.success && res.data) {
                                      setChatbotCustomers(prev => [{ id: res.data!.id, email: res.data!.email, created_at: new Date().toISOString() }, ...prev]);
                                      setNewCustomerEmail(''); setNewCustomerPassword(''); setShowCreateCustomer(false);
                                      setCustomerMessage(''); setCustPwdValidation({ length: false, uppercase: false, lowercase: false, number: false, special: false });
                                    } else { setCustomerMessage(res.error || 'Failed to create customer'); }
                                  }
                                } catch { setCustomerMessage('Failed to create customer'); }
                                finally { setCustomerSaving(false); }
                              }}>{customerSaving ? (customerInviteMode ? 'Sending...' : 'Creating...') : (customerInviteMode ? 'Send Invite' : 'Create')}</button>
                            <button className="btn" style={{ fontSize: '13px', padding: '6px 14px' }} onClick={() => { setShowCreateCustomer(false); setNewCustomerEmail(''); setNewCustomerPassword(''); setCustomerMessage(''); setCustPwdValidation({ length: false, uppercase: false, lowercase: false, number: false, special: false }); }}>Cancel</button>
                            {customerMessage && <span style={{ fontSize: '13px', color: customerMessage.includes('success') ? 'var(--color-success)' : 'var(--color-danger)' }}>{customerMessage}</span>}
                          </div>
                        </div>
                      ) : (
                        <button className="btn btn-primary" onClick={() => setShowCreateCustomer(true)}>
                          + Create Customer Account
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
        )}

        {/* ===== CHATBOT TAB ===== */}
        {activeTab === 'chatbot' && (
          <>
            {isCustomer && (
              <div className="section-intro">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                <span>Configure how your chat widget looks and behaves on your website. Visitors will see this when they click the chat icon.</span>
              </div>
            )}
            {/* Basic Info Card */}
            <div className="card">
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '24px' }}>{cl('Basic Info', 'Chat Widget Settings')}</h3>
              <form onSubmit={handleSave}>
                <DraftBanner draft={chatbotDirty.draft} message="You have unsaved changes from a previous session." onRestore={restoreChatbotDraft} onDismiss={chatbotDirty.clearDraft} />
                <div className="form-row">
                  <div className="form-group">
                    <label style={{ fontSize: '16px', fontWeight: 600, marginBottom: '10px', display: 'block' }}>Chatbot Name</label>
                    <input type="text" className="input" value={name} onChange={(e) => setName(e.target.value)} required style={{ fontSize: '16px', padding: '12px 16px' }} placeholder="e.g., Customer Support Bot" />
                    <small style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginTop: '8px', display: 'block' }}>Internal name for your reference</small>
                  </div>
                  <div className="form-group">
                    <label style={{ fontSize: '16px', fontWeight: 600, marginBottom: '10px', display: 'block' }}>Display Name</label>
                    <input type="text" className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Leave empty to use chatbot name" style={{ fontSize: '16px', padding: '12px 16px' }} />
                    <small style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginTop: '8px', display: 'block' }}>Shown at the top of the chat window</small>
                  </div>
                </div>
                <div className="form-group">
                  <label style={{ fontSize: '16px', fontWeight: 600, marginBottom: '10px', display: 'block' }}>Welcome Message</label>
                  <input type="text" className="input" value={greeting} onChange={(e) => setGreeting(e.target.value)} placeholder="Hello! How can I help you today?" style={{ fontSize: '16px', padding: '12px 16px' }} />
                </div>
                <div className="divider" />
                <div className="section-header mb-4">
                  <h3 style={{ fontSize: '18px', fontWeight: 600 }}>{cl('Action Button (Optional)', 'Link Button (Optional)')}</h3>
                  <p className="text-secondary" style={{ fontSize: '15px', marginTop: '8px' }}>{cl('Add a button that takes customers to a specific page', 'Add a clickable button in the chat window, like a link to your contact page')}</p>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label style={{ fontSize: '16px', fontWeight: 600, marginBottom: '10px', display: 'block' }}>{cl('Button Text', 'Button Label')}</label>
                    <input type="text" className="input" value={ctaLabel} onChange={(e) => setCtaLabel(e.target.value)} placeholder={cl('e.g., Learn More', "e.g., Visit Our Store")} style={{ fontSize: '16px', padding: '12px 16px' }} />
                  </div>
                  <div className="form-group">
                    <label style={{ fontSize: '16px', fontWeight: 600, marginBottom: '10px', display: 'block' }}>{cl('Button Link', 'Button URL')}</label>
                    <input type="url" className="input" value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} placeholder="e.g., https://yourwebsite.com/contact" style={{ fontSize: '16px', padding: '12px 16px' }} />
                  </div>
                </div>
                <div className="flex flex-center gap-3">
                  <button type="submit" className="btn btn-primary" disabled={saving} style={{ fontSize: '16px', padding: '14px 28px', fontWeight: 600 }}>
                    {saving ? <><span className="spinner spinner-sm" /> Saving...</> : <><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17,21 17,13 7,13 7,21" /><polyline points="7,3 7,8 15,8" /></svg> Save</>}
                  </button>
                  {saveMessage && activeTab === 'chatbot' && <span className={saveMessage.includes('success') ? 'success' : 'error'} style={{ fontSize: '15px', fontWeight: 600 }}>{saveMessage}</span>}
                  {!saveMessage && chatbotDirty.isDirty && <span className="unsaved-indicator">Unsaved changes</span>}
                </div>
              </form>
            </div>

            {/* Appearance Card */}
            <div className="card" style={{ marginTop: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '24px' }}>Appearance</h3>
              <form onSubmit={handleAppearanceSave}>
                <DraftBanner draft={appearanceDirty.draft} message="You have unsaved appearance changes from a previous session." onRestore={restoreAppearanceDraft} onDismiss={appearanceDirty.clearDraft} />
                <div className="appearance-layout">
                  <div className="appearance-controls">
                <div className="form-group">
                  <label>Logo URL{isCustomer && <InfoTip text="A web link to your company logo image. It appears in the chat window header." />}</label>
                  <input type="url" className="input" value={logoUrl} onChange={(e) => { setLogoUrl(e.target.value); if (e.target.value.trim().startsWith('file://') || e.target.value.trim().startsWith('file:')) { setAppearanceToast({ message: 'File URLs cannot be accessed from the web.', type: 'warning' }); setTimeout(() => setAppearanceToast(null), 5000); } }} placeholder="https://example.com/logo.png" />
                  <small>Displayed in the chat header and can also be used in the floating launcher.</small>
                </div>
                <div className="divider" />
                <div className="section-header mb-4"><h3 className="text-lg font-semibold">Brand Layout{isCustomer && <InfoTip text="Adjust the header, logo, launcher, and chat window size without changing your colors." />}</h3></div>
                <button type="button" className="btn btn-secondary btn-small" style={{ marginBottom: '16px' }} onClick={() => setTheme((prev) => ({ ...prev, ...RECOMMENDED_BRANDING }))}>
                  Apply Recommended Layout
                </button>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '18px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Header Size</label>
                    {renderSegmentedControl(HEADER_SIZE_OPTIONS, theme.headerSize, (value) => handleThemeOptionChange('headerSize', value))}
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Header Logo</label>
                    {renderSegmentedControl(LOGO_LAYOUT_OPTIONS, theme.logoLayout, (value) => handleThemeOptionChange('logoLayout', value))}
                    {theme.logoLayout === 'wide' && <small style={{ display: 'block', marginTop: '8px' }}>Uses the logo across the header and hides the display name.</small>}
                  </div>
                  {theme.logoLayout === 'wide' && (
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Wide Logo Alignment</label>
                      {renderSegmentedControl(WIDE_LOGO_ALIGN_OPTIONS, theme.wideLogoAlign, (value) => handleThemeOptionChange('wideLogoAlign', value))}
                    </div>
                  )}
                  {theme.logoLayout !== 'wide' && (
                    <>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Logo Size</label>
                    {renderSegmentedControl(LOGO_SIZE_OPTIONS, theme.logoSize, (value) => handleThemeOptionChange('logoSize', value))}
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Logo Shape</label>
                    {renderSegmentedControl(LOGO_SHAPE_OPTIONS, theme.logoShape, (value) => handleThemeOptionChange('logoShape', value))}
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Logo Background</label>
                    {renderSegmentedControl(LOGO_BACKGROUND_OPTIONS, theme.logoBackground, (value) => handleThemeOptionChange('logoBackground', value))}
                  </div>
                    </>
                  )}
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Launcher</label>
                    {renderSegmentedControl(LAUNCHER_STYLE_OPTIONS, theme.launcherStyle, (value) => handleThemeOptionChange('launcherStyle', value))}
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Panel Size</label>
                    {renderSegmentedControl(PANEL_SIZE_OPTIONS, theme.panelSize, (value) => handleThemeOptionChange('panelSize', value))}
                  </div>
                </div>
                <div className="divider" />
                <div className="section-header mb-4"><h3 className="text-lg font-semibold">Color Theme{isCustomer && <InfoTip text="Choose colors for your chat window. Pick a preset or customize individual colors." />}</h3></div>
                <div className="form-group">
                  <label>Color Preset</label>
                  <div className="preset-grid mt-2">
                    {(Object.keys(THEME_PRESETS) as ThemePresetKey[]).map((key) => (
                      <div key={key} onClick={() => handlePresetChange(key)} className={`preset-card ${selectedPreset === key ? 'active' : ''}`}>
                        <div style={{ width: '100%', height: '40px', borderRadius: '6px', background: `linear-gradient(135deg, ${THEME_PRESETS[key].headerBgColor}, ${THEME_PRESETS[key].userBubbleColor})`, marginBottom: '8px' }} />
                        <span className="text-sm font-medium">{THEME_PRESETS[key].name}</span>
                      </div>
                    ))}
                    <div onClick={() => handlePresetChange('custom')} className={`preset-card ${selectedPreset === 'custom' ? 'active' : ''}`}>
                      <div style={{ width: '100%', height: '40px', borderRadius: '6px', background: 'linear-gradient(135deg, #ccc, #999)', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><circle cx="13.5" cy="6.5" r="2.5" /><circle cx="19" cy="17" r="2" /><circle cx="6" cy="12" r="3" /></svg>
                      </div>
                      <span className="text-sm font-medium">Custom</span>
                    </div>
                  </div>
                </div>
                <div className="divider" />
                <div className="section-header mb-4"><h3 className="text-lg font-semibold">Custom Colors</h3></div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' }}>
                  {CUSTOM_COLOR_FIELDS.map(([key, label]) => (
                    <div key={key} className="form-group" style={{ margin: 0 }}>
                      <label>{label}</label>
                      <div className="color-picker-container">
                        <input type="color" value={theme[key]} onChange={(e) => handleThemeColorChange(key, e.target.value)} className="color-swatch" style={{ background: theme[key] }} />
                        <input type="text" className="input" value={theme[key]} onChange={(e) => handleThemeColorChange(key, e.target.value)} style={{ flex: 1 }} />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="appearance-savebar">
                  <button type="submit" className="btn btn-primary" disabled={appearanceSaving}>
                    {appearanceSaving ? <><span className="spinner spinner-sm" /> Saving...</> : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17,21 17,13 7,13 7,21" /><polyline points="7,3 7,8 15,8" /></svg> Save Appearance</>}
                  </button>
                  {appearanceMessage && <span className={appearanceMessage.includes('success') ? 'success' : 'error'}>{appearanceMessage}</span>}
                  {!appearanceMessage && appearanceDirty.isDirty && <span className="unsaved-indicator">Unsaved changes</span>}
                </div>
                  </div>
                  <aside className="appearance-preview">
                    <div className="section-header mb-4"><h3 className="text-lg font-semibold">Live Preview</h3></div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '18px', flexWrap: 'wrap' }}>
                  <div style={{ border: '1px solid var(--color-border)', borderRadius: '12px', overflow: 'hidden', width: `${previewPanelWidth}px`, maxWidth: '100%', boxShadow: 'var(--shadow-lg)' }}>
                    <div style={{ background: theme.headerBgColor, color: theme.headerTextColor, minHeight: `${HEADER_HEIGHTS[theme.headerSize]}px`, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
                      {previewWideLogo ? (
                        <span style={previewWideLogoFrameStyle}>
                          <img src={logoUrl} alt={title || chatbot.name} style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: previewWideLogoObjectPosition, display: 'block' }} />
                        </span>
                      ) : (
                        <>
                          {logoUrl && (
                            <span style={{ width: `${previewLogoFrameSize}px`, height: `${previewLogoFrameSize}px`, flex: `0 0 ${previewLogoFrameSize}px`, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: previewLogoRadius, background: previewLogoBackground, border: theme.logoBackground === 'none' ? 'none' : '1px solid rgba(255,255,255,0.24)', overflow: 'hidden' }}>
                              <img src={logoUrl} alt="" style={{ width: `${previewLogoSize}px`, height: `${previewLogoSize}px`, objectFit: 'contain', borderRadius: theme.logoShape === 'circle' ? '50%' : '6px' }} />
                            </span>
                          )}
                          <span style={{ fontWeight: 600, fontSize: theme.headerSize === 'showcase' ? '17px' : '16px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title || chatbot.name}</span>
                        </>
                      )}
                      <span style={{ marginLeft: 'auto', opacity: 0.8, fontSize: '18px', position: 'relative', zIndex: 1 }}>&times;</span>
                    </div>
                    <div style={{ padding: '16px', background: theme.chatBgColor }}>
                      <div style={{ background: theme.assistantBubbleColor, color: theme.assistantTextColor, padding: '10px 14px', borderRadius: '14px', marginBottom: '10px', maxWidth: '85%', fontSize: '14px' }}>Hello! How can I help?</div>
                      <div style={{ background: theme.userBubbleColor, color: theme.userTextColor, padding: '10px 14px', borderRadius: '14px', marginLeft: 'auto', maxWidth: '85%', textAlign: 'right', fontSize: '14px' }}>Hi there!</div>
                    </div>
                    {ctaLabel && ctaUrl && (
                      <div style={{ padding: '10px 14px', background: 'var(--color-bg-secondary)', borderTop: '1px solid var(--color-border-light)', textAlign: 'center', color: theme.primaryColor, fontSize: '13px', fontWeight: 600 }}>
                        {ctaLabel}
                      </div>
                    )}
                    <div style={{ padding: '8px 14px', background: 'var(--color-bg-secondary)', borderTop: '1px solid var(--color-border-light)', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '10px' }}>
                      Powered by Neural Intelligence
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: theme.primaryColor, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', boxShadow: 'var(--shadow-lg)', position: 'relative' }}>
                      {logoUrl && theme.launcherStyle !== 'icon' ? (
                        <img src={logoUrl} alt="" style={{ width: '42px', height: '42px', objectFit: 'contain', borderRadius: '10px', background: '#fff', padding: '4px' }} />
                      ) : (
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/></svg>
                      )}
                      {logoUrl && theme.launcherStyle === 'logo-with-chat' && (
                        <span style={{ position: 'absolute', right: '-2px', bottom: '-2px', width: '24px', height: '24px', borderRadius: '50%', background: '#fff', border: `2px solid ${theme.primaryColor}`, color: theme.primaryColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/></svg>
                        </span>
                      )}
                    </div>
                    <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>Launcher</span>
                  </div>
                </div>
                  </aside>
                </div>
              </form>
            </div>

            {/* Domains Card */}
              <div className="card" style={{ marginTop: '24px' }}>
                <div className="section-header mb-4">
                  <h3 className="text-lg font-semibold">Allowed Domains</h3>
                  <p className="text-secondary text-sm mt-1">Specify which domains can embed your chatbot widget</p>
                </div>
                <div style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-light)', borderRadius: 'var(--radius-md)', padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
                  <p style={{ fontSize: '12px', color: 'var(--color-text-secondary)', margin: 0, lineHeight: 1.5 }}>
                    Just paste your website URL! We'll add both the main domain and all subdomains.
                  </p>
                </div>
                <form onSubmit={handleAddDomain} className="flex gap-3 mb-6">
                  <input type="text" className="input flex-1" placeholder="Paste your website URL (e.g., https://example.com)" value={newDomain} onChange={(e) => setNewDomain(e.target.value)} />
                  <button type="submit" className="btn btn-primary" style={{ whiteSpace: 'nowrap' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    Add Domain
                  </button>
                </form>
                {chatbot.domains.length === 0 ? (
                  <div className="alert alert-info"><span>No domains configured. Widget will only work on localhost for testing.</span></div>
                ) : (
                  <div>{chatbot.domains.map((domain) => (
                    <div key={domain} className="list-item">
                      <div className="flex flex-center gap-3">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>
                        <span className="font-medium">{domain}</span>
                      </div>
                      <button onClick={() => handleRemoveDomain(domain)} className="btn btn-ghost btn-small" style={{ color: 'var(--color-danger)' }}>Remove</button>
                    </div>
                  ))}</div>
                )}
              </div>

            {/* Email Reports Card */}
              <div className="card" style={{ marginTop: '24px' }}>
                <div className="section-header mb-4">
                  <h3 className="text-lg font-semibold">Email Reports</h3>
                  <p className="text-secondary text-sm mt-1">Receive conversation transcripts via email</p>
                </div>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  const emailInput = e.currentTarget.elements.namedItem('reportEmail') as HTMLInputElement;
                  const email = emailInput.value.trim();
                  if (!email) return;
                  try {
                    const result = await addReportEmail(chatbot.id, email);
                    if (result.success && result.data) { setChatbot({ ...chatbot, report_emails: result.data.emails }); emailInput.value = ''; }
                    else { toast.error(result.error || 'Failed to add email'); }
                  } catch (error) { toast.error('Failed to add email.'); }
                }} className="flex gap-3 mb-6">
                  <input type="email" name="reportEmail" className="input flex-1" placeholder="recipient@example.com" />
                  <button type="submit" className="btn btn-primary">Add Email</button>
                </form>
                {!chatbot.report_emails || chatbot.report_emails.length === 0 ? (
                  <div className="alert alert-info"><span>No email recipients configured.</span></div>
                ) : (
                  <div>{chatbot.report_emails.map((email) => (
                    <div key={email} className="list-item">
                      <div className="flex flex-center gap-3">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
                        <span className="font-medium">{email}</span>
                      </div>
                      <button onClick={async () => { try { const result = await removeReportEmail(chatbot.id, email); if (result.success && result.data) setChatbot({ ...chatbot, report_emails: result.data.emails }); } catch {} }} className="btn btn-ghost btn-small" style={{ color: 'var(--color-danger)' }}>Remove</button>
                    </div>
                  ))}</div>
                )}
              </div>

            {/* Embed Code Card */}
              <div className="card" style={{ marginTop: '24px' }}>
                <div className="section-header mb-4">
                  <h3 className="text-lg font-semibold">Embed Code</h3>
                  <p className="text-secondary text-sm mt-1">Add this script to your website to display the chatbot widget</p>
                </div>
                <div className="alert alert-info mb-4">
                  <span>Paste this code just before the closing <code style={{ background: 'rgba(0,0,0,0.1)', padding: '2px 6px', borderRadius: '4px' }}>&lt;/body&gt;</code> tag on your website.</span>
                </div>
                <div className="code-block">{getEmbedSnippet()}</div>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <button onClick={() => copyToClipboard(getEmbedSnippet())} className="btn btn-primary mt-4">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                    Copy to Clipboard
                  </button>
                  {copied && <div style={{ position: 'absolute', top: '-40px', left: '50%', transform: 'translateX(-50%)', background: 'var(--color-success)', color: 'white', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '500', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', whiteSpace: 'nowrap', animation: 'fadeIn 0.2s ease-in-out' }}>Copied!</div>}
                </div>
              </div>

            {/* Delete Chatbot (admin only) */}
            {user?.role === 'admin' && (
              <div className="card" style={{ marginTop: '24px', borderColor: 'var(--color-danger-border)' }}>
                <div className="section-header mb-4">
                  <h3 className="text-lg font-semibold" style={{ color: 'var(--color-danger)' }}>Danger Zone</h3>
                </div>
                <button className="btn btn-danger" onClick={async () => {
                  const customerCount = chatbotCustomers.length;
                  const customerWarning = customerCount > 0 ? ` This will also delete ${customerCount} customer account${customerCount !== 1 ? 's' : ''}.` : '';
                  if (!await confirm({ title: 'Delete Chatbot', description: `Are you sure you want to delete "${chatbot.name}"?${customerWarning} This action cannot be undone.`, confirmLabel: 'Delete', destructive: true })) return;
                  try {
                    const result = await deleteChatbot(id!);
                    if (result.success) { skipGuardRef.current = true; navigate('/'); }
                    else toast.error(result.error || 'Failed to delete');
                  } catch { toast.error('Failed to delete chatbot'); }
                }}>Delete Chatbot</button>
              </div>
            )}

            {showWidgetHint && (
              <div className="widget-hint">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                <span className="widget-hint-text">Try your chatbot! Click the chat icon below to test it.</span>
                <button className="widget-hint-dismiss" onClick={dismissWidgetHint} title="Dismiss">&times;</button>
              </div>
            )}
          </>
        )}

        {/* ===== KNOWLEDGE BASE TAB ===== */}
        {activeTab === 'knowledge-base' && (
          <>
            {isCustomer && (
              <div className="section-intro">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></svg>
                <span>This is the information your AI uses to answer questions. The more details you provide, the better it responds — for both the chat widget and email replies.</span>
              </div>
            )}
            {/* Business Description */}
            <div className="card">
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>{cl('Business Description', 'About Your Business')}</h3>
              <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '24px' }}>{cl('This is the core information your AI uses to answer questions in both the chatbot and email responder.', 'Tell the AI about your business so it can answer customer questions accurately.')}</p>
              <form onSubmit={handleKnowledgeSave}>
                <DraftBanner draft={knowledgeDirty.draft} message="You have unsaved knowledge base changes from a previous session." onRestore={restoreKnowledgeDraft} onDismiss={knowledgeDirty.clearDraft} />
                <div className="form-group">
                  <div style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-light)', borderRadius: 'var(--radius-md)', padding: 'var(--space-4)', marginBottom: 'var(--space-3)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>
                      <span style={{ fontWeight: 500, fontSize: '13px' }}>Auto-fill from website</span>
                    </div>
                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                      <input type="url" className="input" value={websiteUrl} onChange={(e) => { setWebsiteUrl(e.target.value); setExtractError(''); }} placeholder="https://yourcompany.com" style={{ flex: 1 }} disabled={extracting} />
                      <button type="button" className="btn btn-secondary" onClick={handleExtractWebsite} disabled={extracting || !websiteUrl.trim()} style={{ whiteSpace: 'nowrap' }}>
                        {extracting ? <><span className="spinner spinner-sm" /> Extracting...</> : 'Extract'}
                      </button>
                    </div>
                    {extractError && <p style={{ fontSize: '12px', color: 'var(--color-danger)', marginTop: 'var(--space-2)' }}>{extractError}</p>}
                  </div>
                  <textarea className="input textarea" value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={6} placeholder={cl('Tell us about your company, products, and services...', 'Describe your business, services, hours, pricing, policies \u2014 anything you\'d want the AI to know...')} style={{ fontSize: '16px', padding: '12px 16px', lineHeight: '1.6' }} />
                  <small style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginTop: '8px', display: 'block' }}>This description powers both the chatbot widget and the email responder.</small>
                </div>
                <div className="flex flex-center gap-3">
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? <><span className="spinner spinner-sm" /> Saving...</> : 'Save Description'}
                  </button>
                  {saveMessage && activeTab === 'knowledge-base' && <span className={saveMessage.includes('success') ? 'success' : 'error'} style={{ fontSize: '15px', fontWeight: 600 }}>{saveMessage}</span>}
                  {!saveMessage && knowledgeDirty.isDirty && <span className="unsaved-indicator">Unsaved changes</span>}
                </div>
              </form>
            </div>

            {/* Documents */}
            <div className="card" style={{ marginTop: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Documents</h3>
              <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '24px' }}>{cl('Upload PDFs to give the AI knowledge about your business.', 'Upload product catalogs, menus, price lists, or any PDF with information the AI should know.')}</p>
              <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={() => !uploading && fileInputRef.current?.click()} style={{ border: `2px dashed ${isDragging ? 'var(--color-primary)' : 'var(--color-border)'}`, borderRadius: 'var(--radius-lg)', padding: '40px 20px', textAlign: 'center', backgroundColor: isDragging ? 'var(--color-primary-light)' : 'var(--color-bg-secondary)', cursor: uploading ? 'not-allowed' : 'pointer', transition: 'all var(--transition-normal)', marginBottom: '24px' }}>
                <input type="file" ref={fileInputRef} accept=".pdf" multiple onChange={handleFileUpload} style={{ display: 'none' }} />
                {uploading ? (
                  <div><div className="spinner spinner-lg" style={{ margin: '0 auto 16px' }} /><p style={{ color: 'var(--color-text-primary)', fontSize: '16px', fontWeight: 500 }}>Uploading {uploadingCount} PDF{uploadingCount === 1 ? '' : 's'}...</p></div>
                ) : (
                  <div>
                    <div style={{ width: '56px', height: '56px', margin: '0 auto 16px', backgroundColor: isDragging ? 'var(--color-primary)' : 'var(--color-border)', borderRadius: 'var(--radius-lg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={isDragging ? 'white' : 'var(--color-text-muted)'} strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17,8 12,3 7,8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                    </div>
                    <p style={{ color: 'var(--color-text-primary)', fontSize: '16px', fontWeight: 500 }}>{isDragging ? 'Drop your PDFs here' : 'Drag & drop PDFs here'}</p>
                    <p style={{ color: 'var(--color-text-muted)', fontSize: '14px', marginTop: '4px' }}>or click to browse files</p>
                  </div>
                )}
              </div>
              {files.length === 0 ? (
                <div className="empty-state" style={{ padding: 'var(--space-8) var(--space-4)' }}>
                  <h3 className="empty-state-title">{cl('No files uploaded yet', 'No documents uploaded yet')}</h3>
                  <p className="empty-state-description">{cl('Upload PDF files to expand the AI\'s knowledge base.', 'Upload a PDF like a product catalog, price list, or FAQ document to give your AI more knowledge.')}</p>
                </div>
              ) : (
                <div>
                  <div className="section-title mb-3">Uploaded Files ({files.length})</div>
                  {files.map((file) => (
                    <div key={file.id} style={{ display: 'flex', alignItems: 'center', padding: 'var(--space-3) var(--space-4)', backgroundColor: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-2)', gap: 'var(--space-3)' }}>
                      <div style={{ width: '40px', height: '40px', background: 'linear-gradient(135deg, #ef4444, #dc2626)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <span style={{ color: 'white', fontSize: '11px', fontWeight: 700 }}>PDF</span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', margin: 0 }}>{file.filename}</p>
                        <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', margin: 0 }}>{new Date(file.created_at).toLocaleDateString()}</p>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        {file.status === 'in_progress' ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}><span className="spinner spinner-sm" style={{ borderTopColor: 'var(--color-warning)' }} /><span style={{ color: 'var(--color-warning)', fontSize: '13px', fontWeight: 500 }}>Processing</span></div>
                        ) : file.status === 'completed' ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2.5"><polyline points="20,6 9,17 4,12" /></svg><span style={{ color: 'var(--color-success)', fontSize: '13px', fontWeight: 500 }}>Ready</span></div>
                        ) : (
                          <span style={{ color: 'var(--color-danger)', fontSize: '13px', fontWeight: 500 }}>{file.status}</span>
                        )}
                      </div>
                      <button onClick={() => handleDeleteFile(file.id, file.filename)} className="btn btn-ghost btn-icon" style={{ color: 'var(--color-text-muted)' }} title="Delete file">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3,6 5,6 21,6" /><path d="M19,6v14a2,2 0 0 1-2,2H7a2,2 0 0 1-2-2V6m3,0V4a2,2 0 0 1,2-2h4a2,2 0 0 1,2,2v2" /></svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* FAQs */}
            <div className="card" style={{ marginTop: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>{cl('FAQs', 'Frequently Asked Questions')}</h3>
              <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '24px' }}>{cl('Common questions and answers the AI can reference.', 'Add questions your customers commonly ask. The AI uses these to give accurate answers.')}</p>
              <form onSubmit={handleAddFaq} style={{ marginBottom: 'var(--space-6)', padding: 'var(--space-5)', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border-light)' }}>
                <h4 className="font-semibold mb-4">Add New FAQ</h4>
                <div className="form-group">
                  <label>Question</label>
                  <input type="text" className="input" value={newFaqQuestion} onChange={(e) => setNewFaqQuestion(e.target.value)} placeholder="e.g., What are your business hours?" />
                </div>
                <div className="form-group">
                  <label>Answer</label>
                  <textarea className="input textarea" value={newFaqAnswer} onChange={(e) => setNewFaqAnswer(e.target.value)} placeholder="e.g., We are open Monday through Friday, 9am to 5pm." rows={3} />
                </div>
                <button type="submit" className="btn btn-primary" disabled={faqSaving || !newFaqQuestion.trim() || !newFaqAnswer.trim()}>
                  {faqSaving ? <><span className="spinner spinner-sm" /> Adding...</> : 'Add FAQ'}
                </button>
              </form>
              {faqs.length === 0 ? (
                <div className="alert alert-info"><span>{cl('No FAQs yet. Add your first one above.', 'No questions added yet. Add common questions your customers ask, like business hours or return policies.')}</span></div>
              ) : (
                <div>
                  <div className="section-title mb-3">FAQ Entries ({faqs.length})</div>
                  {faqs.map((faq) => (
                    <div key={faq.id} style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 'var(--space-4)', marginBottom: 'var(--space-3)' }}>
                      {editingFaq?.id === faq.id ? (
                        <>
                          <div className="form-group"><label>Question</label><input type="text" className="input" value={editingFaq.question} onChange={(e) => setEditingFaq({ ...editingFaq, question: e.target.value })} /></div>
                          <div className="form-group"><label>Answer</label><textarea className="input textarea" value={editingFaq.answer} onChange={(e) => setEditingFaq({ ...editingFaq, answer: e.target.value })} rows={3} /></div>
                          <div className="flex gap-2">
                            <button onClick={() => handleUpdateFaq(editingFaq)} className="btn btn-primary btn-small" disabled={faqSaving}>{faqSaving ? 'Saving...' : 'Save'}</button>
                            <button onClick={() => setEditingFaq(null)} className="btn btn-secondary btn-small">Cancel</button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ marginBottom: 'var(--space-3)' }}>
                            <div style={{ fontWeight: 600, marginBottom: 'var(--space-2)' }}>{faq.question}</div>
                            <p style={{ margin: 0, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>{faq.answer}</p>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => setEditingFaq(faq)} className="btn btn-secondary btn-small">Edit</button>
                            <button onClick={() => handleDeleteFaq(faq.id)} className="btn btn-danger btn-small">Delete</button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* ===== EMAIL RESPONDER TAB ===== */}
        {activeTab === 'email-responder' && (
          <>
            {isCustomer && (
              <div className="section-intro">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
                <span>The email responder reads incoming emails and drafts AI-powered replies for you to review before sending.</span>
              </div>
            )}
            {!emailTabLoaded ? (
              <div className="card" style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div className="spinner" style={{ margin: '0 auto 16px' }} />
                <p style={{ color: 'var(--color-text-secondary)' }}>Loading email responder...</p>
              </div>
            ) : (
              <>
                {/* Email Connection */}
                <div className="card">
                  <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>{cl('Email Account', 'Connected Email')}</h3>
                  <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '24px' }}>{isSales ? 'View the connected email account for this chatbot.' : 'Connect an email account so the AI can draft responses to incoming emails.'}</p>
                  {isAdmin && (
                    <div className="alert alert-warning" style={{ marginBottom: '16px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: '1px' }}>
                        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                        <line x1="12" y1="9" x2="12" y2="13"/>
                        <line x1="12" y1="17" x2="12.01" y2="17"/>
                      </svg>
                      <span style={{ fontSize: '13px' }}>Connecting or disconnecting here acts on behalf of the customer and will affect their email integration.</span>
                    </div>
                  )}
                  {connectionMessage && (
                    <div style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: '8px', background: connectionMessage.type === 'success' ? 'var(--color-success-light)' : 'var(--color-danger-light)', color: connectionMessage.type === 'success' ? 'var(--color-success)' : 'var(--color-danger)', fontSize: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{connectionMessage.text}</span>
                      <button onClick={() => setConnectionMessage(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '18px', lineHeight: 1 }}>&times;</button>
                    </div>
                  )}
                  {emailConnection ? (
                    <div style={{ padding: '20px', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border-light)' }}>
                      <div className="email-connection-header">
                        <div className="email-connection-identity">
                          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: emailConnection.provider === 'forwarding' ? (emailConnection.status === 'active' ? 'var(--color-success-light)' : emailConnection.status === 'pending' ? 'var(--color-warning-light)' : emailConnection.status === 'error' ? 'var(--color-danger-light)' : 'var(--color-bg-secondary)') : 'var(--color-bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {emailConnection.provider === 'gmail' ? <img src="/gmail.svg" alt="Gmail" width="20" height="20" /> :
                             emailConnection.provider === 'outlook' ? <img src="/outlook.svg" alt="Outlook" width="20" height="20" /> :
                             <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={emailConnection.status === 'active' ? 'var(--color-success)' : emailConnection.status === 'pending' ? 'var(--color-warning)' : emailConnection.status === 'error' ? 'var(--color-danger)' : 'var(--color-text-secondary)'} strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600 }}>Connected via {emailConnection.provider!.charAt(0).toUpperCase() + emailConnection.provider!.slice(1)}</div>
                            <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)', overflowWrap: 'anywhere' }}>
                              {emailConnection.email_address || emailConnection.forwarding_address || 'Pending setup'}
                            </div>
                          </div>
                        </div>
                        <div className="email-connection-actions">
                          <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: 500, background: emailConnection.status === 'active' ? 'var(--color-success-light)' : emailConnection.status === 'pending' ? 'var(--color-warning-light)' : emailConnection.status === 'error' ? 'var(--color-danger-light)' : 'var(--color-bg-secondary)', color: emailConnection.status === 'active' ? 'var(--color-success)' : emailConnection.status === 'pending' ? 'var(--color-warning)' : emailConnection.status === 'error' ? 'var(--color-danger)' : 'var(--color-text-secondary)' }}>
                            {emailConnection.status}
                          </span>
                          {!isSales && (
                            <div className="email-test-actions" aria-label="Email connection test actions">
                              <button type="button" onClick={handleRunEmailDiagnostics} className="btn btn-secondary btn-small email-test-button" disabled={emailDiagnosticsLoading}>
                                {emailDiagnosticsLoading ? <><span className="spinner spinner-sm" /> Testing...</> : 'Test connection'}
                              </button>
                              {emailConnection.provider !== 'forwarding' && (
                                <>
                                  <button type="button" onClick={handleCreateEmailDiagnosticTest} className="btn btn-secondary btn-small email-test-button" disabled={emailDiagnosticTestLoading}>
                                    {emailDiagnosticTestLoading ? <><span className="spinner spinner-sm" /> Sending...</> : 'Send test email'}
                                  </button>
                                  <button type="button" onClick={handleTestEmailDraftCreation} className="btn btn-secondary btn-small email-test-button" disabled={emailDraftTestLoading}>
                                    {emailDraftTestLoading ? <><span className="spinner spinner-sm" /> Creating...</> : 'Test draft creation'}
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                          {!isSales && (
                            <button onClick={handleDisconnectEmail} className={`btn ${isAdmin ? 'btn-warning' : 'btn-danger'} btn-small`}>
                              {isAdmin ? 'Disconnect (Admin)' : 'Disconnect'}
                            </button>
                          )}
                        </div>
                      </div>
                      {emailConnection.provider === 'forwarding' && emailConnection.forwarding_address && (
                        <div style={{ marginTop: '16px', padding: '12px 16px', background: 'var(--color-bg-white)', borderRadius: '8px', border: '1px solid var(--color-border)' }}>
                          <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginBottom: '4px' }}>Forward emails to:</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <code style={{ fontSize: '14px', fontWeight: 600 }}>{emailConnection.forwarding_address}</code>
                            <div style={{ position: 'relative' }}>
                              <button onClick={() => copyToClipboard(emailConnection.forwarding_address!)} className="btn btn-secondary btn-small" title="Copy address" style={{ padding: '4px 8px', minHeight: 'auto' }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                              </button>
                              {copied && <div style={{ position: 'absolute', top: '-40px', left: '50%', transform: 'translateX(-50%)', background: 'var(--color-success)', color: 'white', padding: '6px 12px', borderRadius: '6px', fontSize: '13px', fontWeight: '500', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', whiteSpace: 'nowrap', animation: 'fadeIn 0.2s ease-in-out' }}>Copied!</div>}
                            </div>
                          </div>
                        </div>
                      )}
                      {emailDiagnosticsError && (
                        <div className="alert alert-danger" style={{ marginTop: '16px', fontSize: '13px' }}>
                          {emailDiagnosticsError}
                        </div>
                      )}
                      {emailDiagnosticTestError && (
                        <div className="alert alert-danger" style={{ marginTop: '16px', fontSize: '13px' }}>
                          {emailDiagnosticTestError}
                        </div>
                      )}
                      {emailDraftTestError && (
                        <div className="alert alert-danger" style={{ marginTop: '16px', fontSize: '13px' }}>
                          {emailDraftTestError}
                        </div>
                      )}
                      {emailDraftTest && (
                        <div className="alert alert-success" style={{ marginTop: '16px', fontSize: '13px' }}>
                          <div style={{ fontWeight: 700, marginBottom: '6px' }}>{emailDraftTest.message}</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px' }}>
                            <div>
                              <div style={{ color: 'var(--color-text-secondary)' }}>Recipient</div>
                              <div style={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{emailDraftTest.recipient}</div>
                            </div>
                            <div>
                              <div style={{ color: 'var(--color-text-secondary)' }}>Subject</div>
                              <div style={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{emailDraftTest.subject}</div>
                            </div>
                            <div>
                              <div style={{ color: 'var(--color-text-secondary)' }}>Draft ID</div>
                              <div style={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{emailDraftTest.draft_id}</div>
                            </div>
                          </div>
                        </div>
                      )}
                      {emailDiagnosticTest && (
                        <div style={{ marginTop: '16px', padding: '16px', background: 'var(--color-bg-white)', borderRadius: '8px', border: '1px solid var(--color-border)' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: '14px' }}>Diagnostic test email</div>
                              <div style={{ color: 'var(--color-text-secondary)', fontSize: '13px', marginTop: '2px' }}>{emailDiagnosticTest.instructions}</div>
                            </div>
                            <button type="button" onClick={handleCheckEmailDiagnosticTest} className="btn btn-secondary btn-small" disabled={emailDiagnosticTestChecking}>
                              {emailDiagnosticTestChecking ? <><span className="spinner spinner-sm" /> Checking...</> : 'Check for test email'}
                            </button>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px', marginBottom: '12px' }}>
                            <div style={{ padding: '10px', border: '1px solid var(--color-border-light)', borderRadius: '8px' }}>
                              <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>Recipient</div>
                              <div style={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{emailDiagnosticTest.recipient}</div>
                            </div>
                            <div style={{ padding: '10px', border: '1px solid var(--color-border-light)', borderRadius: '8px' }}>
                              <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>Code</div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <strong>{emailDiagnosticTest.code}</strong>
                                <button type="button" onClick={() => copyToClipboard(emailDiagnosticTest.code)} className="btn btn-secondary btn-small" style={{ padding: '4px 8px', minHeight: 'auto' }}>Copy</button>
                              </div>
                            </div>
                            <div style={{ padding: '10px', border: '1px solid var(--color-border-light)', borderRadius: '8px' }}>
                              <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>Status</div>
                              <div style={{ fontWeight: 700 }}>{emailDiagnosticTest.manual_required ? 'Manual send required' : 'Sent'}</div>
                            </div>
                          </div>
                          {emailDiagnosticTest.manual_required && (
                            <div style={{ display: 'grid', gap: '8px', marginBottom: '12px' }}>
                              <div>
                                <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginBottom: '4px' }}>Subject to send</div>
                                <code style={{ display: 'block', padding: '10px', background: 'var(--color-bg-secondary)', borderRadius: '8px', overflowWrap: 'anywhere' }}>{emailDiagnosticTest.subject}</code>
                              </div>
                              <div>
                                <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginBottom: '4px' }}>Body to send</div>
                                <pre style={{ margin: 0, padding: '10px', background: 'var(--color-bg-secondary)', borderRadius: '8px', whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '13px' }}>{emailDiagnosticTest.body}</pre>
                              </div>
                            </div>
                          )}
                          {emailDiagnosticTestCheck && (
                            <div className={emailDiagnosticTestCheck.outcome === 'found_unread_inbox' ? 'alert alert-success' : emailDiagnosticTestCheck.outcome === 'not_found' ? 'alert alert-warning' : 'alert alert-warning'} style={{ fontSize: '13px', marginBottom: 0 }}>
                              <div style={{ fontWeight: 700, marginBottom: '4px' }}>{emailDiagnosticTestCheck.message}</div>
                              <div>
                                {emailDiagnosticTestCheck.detected_folder ? `Folder: ${emailDiagnosticTestCheck.detected_folder}` : 'Folder: not found'}
                                {emailDiagnosticTestCheck.detected_read_state ? ` - ${formatDiagnosticLabel(emailDiagnosticTestCheck.detected_read_state)}` : ''}
                                {emailDiagnosticTestCheck.received_at ? ` - ${formatDiagnosticDate(emailDiagnosticTestCheck.received_at)}` : ''}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {emailDiagnostics && (
                        <div style={{ marginTop: '16px', padding: '16px', background: 'var(--color-bg-white)', borderRadius: '8px', border: '1px solid var(--color-border)' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '12px', marginBottom: '12px' }}>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: '14px' }}>{emailDiagnostics.provider_error || diagnosticHealthLabel(emailDiagnostics.health)}</div>
                              <div style={{ color: 'var(--color-text-secondary)', fontSize: '13px', marginTop: '2px' }}>
                                Last tested {formatDiagnosticDate(new Date().toISOString())}
                              </div>
                            </div>
                            {emailDiagnostics.provider_error_detail && (
                              <span style={{ color: 'var(--color-danger)', fontSize: '13px', fontWeight: 600 }}>{emailDiagnostics.provider_error_detail}</span>
                            )}
                          </div>
                          {emailDiagnostics.warning && (
                            <div className="alert alert-warning" style={{ marginBottom: '12px', fontSize: '13px' }}>
                              {emailDiagnostics.warning}
                            </div>
                          )}
                          {emailDiagnostics.likely_cause && (
                            <div style={{ marginBottom: '12px', padding: '12px', background: 'var(--color-bg-secondary)', borderRadius: '8px' }}>
                              <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 700, marginBottom: '4px' }}>Likely cause</div>
                              <div style={{ fontSize: '14px', fontWeight: 600 }}>{emailDiagnostics.likely_cause}</div>
                            </div>
                          )}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '12px' }}>
                            <div>
                              <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Responder</div>
                              <div style={{ fontSize: '14px', fontWeight: 600 }}>{emailDiagnostics.responder_enabled ? 'Enabled' : 'Disabled'}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Scan interval</div>
                              <div style={{ fontSize: '14px', fontWeight: 600 }}>{emailDiagnostics.scan_interval_minutes || '-'} min</div>
                            </div>
                            <div>
                              <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Token expires</div>
                              <div style={{ fontSize: '14px', fontWeight: 600 }}>{formatDiagnosticDate(emailDiagnostics.token_expires_at)}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Last scan</div>
                              <div style={{ fontSize: '14px', fontWeight: 600 }}>{formatDiagnosticDate(emailDiagnostics.last_scan?.finished_at || emailDiagnostics.last_scan?.started_at)}</div>
                            </div>
                          </div>
                          {emailDiagnostics.provider_diagnostics?.mailboxIdentity && (
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px' }}>Mailbox identity</div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '8px' }}>
                                {Object.entries(emailDiagnostics.provider_diagnostics.mailboxIdentity).filter(([, value]) => value !== null && value !== '').map(([key, value]) => (
                                  <div key={key} style={{ padding: '10px', border: '1px solid var(--color-border-light)', borderRadius: '8px' }}>
                                    <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>{formatDiagnosticLabel(key)}</div>
                                    <div style={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{String(value)}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {emailDiagnostics.last_scan && (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px', padding: '12px', background: 'var(--color-bg-secondary)', borderRadius: '8px', marginBottom: '12px' }}>
                              {[
                                ['Unread found', emailDiagnostics.last_scan.provider_unread_count],
                                ['Fetched', emailDiagnostics.last_scan.fetched_count],
                                ['Drafts', emailDiagnostics.last_scan.drafts_created_count],
                                ['Duplicates', emailDiagnostics.last_scan.duplicate_skipped_count],
                                ['Newsletters', emailDiagnostics.last_scan.newsletter_skipped_count],
                                ['Errors', emailDiagnostics.last_scan.error_count],
                              ].map(([label, value]) => (
                                <div key={label}>
                                  <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>{label}</div>
                                  <div style={{ fontWeight: 700 }}>{value}</div>
                                </div>
                              ))}
                            </div>
                          )}
                          {(emailDiagnostics.provider_diagnostics?.folders || emailDiagnostics.provider_diagnostics?.labels) && (
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px' }}>Mailbox folders</div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '8px' }}>
                                {(emailDiagnostics.provider_diagnostics.folders || emailDiagnostics.provider_diagnostics.labels || []).map((item: any) => (
                                  <div key={item.folder || item.label} style={{ padding: '10px', border: '1px solid var(--color-border-light)', borderRadius: '8px' }}>
                                    <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginBottom: '6px' }}>{item.displayName || item.folder || item.label}</div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', fontSize: '13px' }}>
                                      <span style={{ color: 'var(--color-text-secondary)' }}>Unread</span>
                                      <strong>{item.unreadItemCount ?? item.messagesUnread ?? 0}</strong>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', fontSize: '13px' }}>
                                      <span style={{ color: 'var(--color-text-secondary)' }}>Total</span>
                                      <strong>{item.totalItemCount ?? item.messagesTotal ?? item.threadsTotal ?? '-'}</strong>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {emailDiagnostics.provider_diagnostics?.recentInboxSamples && (
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px' }}>Recent Inbox activity</div>
                              {renderDiagnosticSamples(emailDiagnostics.provider_diagnostics.recentInboxSamples, 'No recent Inbox messages were visible to the connected account.')}
                            </div>
                          )}
                          {emailDiagnostics.provider_diagnostics?.unreadSamples && (
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px' }}>Unread Inbox messages</div>
                              {renderDiagnosticSamples(emailDiagnostics.provider_diagnostics.unreadSamples, 'No unread Inbox messages were visible to the connected account.')}
                            </div>
                          )}
                          {emailDiagnostics.provider_diagnostics?.draftSamples && (
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px' }}>Draft visibility</div>
                              {emailDiagnostics.provider_diagnostics.draftSamples.length === 0 ? (
                                <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>No recent drafts were visible to the connected account.</div>
                              ) : (
                                <div style={{ display: 'grid', gap: '8px' }}>
                                  {emailDiagnostics.provider_diagnostics.draftSamples.slice(0, 5).map((draft, index) => (
                                    <div key={`${draft.updatedAt || index}`} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '8px 0', borderTop: index === 0 ? 'none' : '1px solid var(--color-border-light)' }}>
                                      <div style={{ minWidth: 0 }}>
                                        <div style={{ fontSize: '13px', fontWeight: 600 }}>{draft.subject || 'Draft'}</div>
                                        <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>{draft.to?.length ? `To ${draft.to.join(', ')}` : 'Recipients hidden'}</div>
                                      </div>
                                      <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>{formatDiagnosticDate(draft.updatedAt)}</div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          {emailDiagnostics.provider_diagnostics?.mailboxSettings && (
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px' }}>Provider settings</div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px' }}>
                                {Object.entries(emailDiagnostics.provider_diagnostics.mailboxSettings).filter(([, value]) => value).map(([key, value]) => (
                                  <div key={key} style={{ padding: '10px', border: '1px solid var(--color-border-light)', borderRadius: '8px' }}>
                                    <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>{formatDiagnosticLabel(key)}</div>
                                    <div style={{ fontWeight: 700 }}>{String(value)}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {emailDiagnostics.provider_diagnostics?.messageRules && emailDiagnostics.provider_diagnostics.messageRules.length > 0 && (
                            <div>
                              <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px' }}>Rules and routing signals</div>
                              <div style={{ display: 'grid', gap: '6px' }}>
                                {emailDiagnostics.provider_diagnostics.messageRules.map((rule, index) => (
                                  <div key={`${rule.displayName || 'rule'}-${index}`} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: '13px' }}>
                                    <span>{rule.displayName || 'Unnamed rule'}</span>
                                    <strong>{rule.isEnabled ? 'Enabled' : 'Disabled'}</strong>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : isSales ? (
                    <div style={{ padding: '20px', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border-light)', textAlign: 'center' }}>
                      <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', margin: 0 }}>No email account connected. Email connection is managed by the account owner or an admin.</p>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' }}>
                      {(['gmail', 'outlook', 'forwarding'] as const).map((provider) => (
                        <button key={provider} onClick={() => handleConnectEmail(provider)} disabled={connectingProvider !== null} className="btn btn-secondary" style={{ padding: '24px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', height: 'auto', ...(isAdmin ? { borderColor: 'var(--color-warning)', borderWidth: '2px' } : {}) }}>
                          {connectingProvider === provider ? <span className="spinner spinner-sm" /> : (
                            provider === 'gmail' ? <img src="/gmail.svg" alt="Gmail" width="24" height="24" /> :
                            provider === 'outlook' ? <img src="/outlook.svg" alt="Outlook" width="24" height="24" /> :
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
                          )}
                          <span style={{ fontWeight: 600 }}>
                            {provider === 'gmail' ? 'Gmail' : provider === 'outlook' ? 'Outlook' : 'Email Forwarding'}
                          </span>
                          <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                            {provider === 'forwarding' ? 'Forward emails to us' : provider === 'gmail' ? 'Connect your Gmail account' : 'Connect your Outlook account'}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Responder Settings */}
                <div className="card" style={{ marginTop: '24px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>{cl('Responder Settings', 'Email Settings')}</h3>
                  <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '24px' }}>{cl('Configure how the AI drafts email responses.', 'Control how the AI writes email replies on your behalf.')}</p>
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: responderToggleDisabled ? 'not-allowed' : 'pointer', opacity: responderToggleDisabled ? 0.6 : 1 }}>
                      <div style={{ position: 'relative', width: '48px', height: '24px', flexShrink: 0 }}>
                        <input type="checkbox" checked={emailConfig?.enabled || false} disabled={responderToggleDisabled} onChange={async (e) => {
                          const newValue = e.target.checked;
                          if (isAdmin && !await confirm({
                            title: newValue ? 'Enable Email Responder' : 'Disable Email Responder',
                            description: newValue
                              ? 'You are enabling the email responder on behalf of the customer.\n\nThis will automatically scan their connected inbox and generate AI draft replies.\n\nAre you sure you want to proceed?'
                              : 'You are disabling the email responder on behalf of the customer.\n\nNew emails will no longer be scanned and no new drafts will be generated.\n\nAre you sure you want to proceed?',
                            confirmLabel: newValue ? 'Enable' : 'Disable',
                          })) return;
                          const prev = emailConfig;
                          setEmailConfig(p => p ? { ...p, enabled: newValue } : { chatbot_id: id!, enabled: newValue, response_tone: 'professional', email_signature: '', scan_interval_minutes: 5, cc_forwarder_on_forward: false, exclude_newsletters: true });
                          setEmailConfigSaving(true);
                          try {
                            const result = await updateEmailResponderConfig(id!, {
                              enabled: newValue,
                              response_tone: prev?.response_tone || 'professional',
                              email_signature: prev?.email_signature || '',
                              scan_interval_minutes: prev?.scan_interval_minutes || 5,
                              cc_forwarder_on_forward: Boolean(prev?.cc_forwarder_on_forward),
                              exclude_newsletters: prev?.exclude_newsletters ?? true,
                            });
                            if (result.success && result.data) setEmailConfig(result.data);
                          } catch {
                            setEmailConfig(prev);
                          } finally { setEmailConfigSaving(false); }
                        }} style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
                        <div style={{ width: '48px', height: '24px', background: emailConfig?.enabled ? 'var(--color-success)' : 'var(--color-text-muted)', borderRadius: '12px', transition: 'background 0.2s ease', position: 'relative' }}>
                          <div style={{ position: 'absolute', top: '2px', left: emailConfig?.enabled ? '26px' : '2px', width: '20px', height: '20px', background: 'white', borderRadius: '50%', transition: 'left 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} />
                        </div>
                      </div>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: '16px' }}>Email Responder{isCustomer && <InfoTip text="When turned on, the AI automatically reads new emails and writes draft replies for you to review." />}</span>
                        <div style={{ fontSize: '13px', color: responderToggleDisabled ? 'var(--color-text-muted)' : 'var(--color-text-secondary)', marginTop: '2px' }}>{responderHelperText}</div>
                      </div>
                    </label>
                    {isAdmin && emailConnection?.connected && !chatbot?.customer_email_responder_enabled && (
                      <div className="alert alert-warning" style={{ marginTop: '8px', fontSize: '13px' }}>
                        Note: The Email Responder tab is not visible to customers. Enable it in Admin Config to give customers access.
                      </div>
                    )}
                  </div>
                  <form onSubmit={handleSaveEmailConfig}>
                    <DraftBanner draft={emailConfigDirty.draft} message="You have unsaved email config changes from a previous session." onRestore={restoreEmailConfigDraft} onDismiss={emailConfigDirty.clearDraft} />
                    <div className="form-row">
                      <div className="form-group">
                        <label>{cl('Response Tone', 'Reply Style')}{isCustomer && <InfoTip text="Controls how formal or casual the AI sounds in email drafts." />}</label>
                        <select className="input" value={emailConfig?.response_tone || 'professional'} onChange={(e) => setEmailConfig(prev => prev ? { ...prev, response_tone: e.target.value as any } : null)}>
                          <option value="formal">Formal</option>
                          <option value="professional">Professional</option>
                          <option value="friendly">Friendly</option>
                          <option value="concise">Concise</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>{cl('Scan Interval (minutes)', 'Check for New Emails Every')}{isCustomer && <InfoTip text="How often the system checks your inbox for new emails to draft replies." />}</label>
                        <input type="number" className="input" min={1} max={60} value={emailConfig?.scan_interval_minutes || 5} onChange={(e) => setEmailConfig(prev => prev ? { ...prev, scan_interval_minutes: parseInt(e.target.value) || 5 } : null)} />
                        {isCustomer && <small style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginTop: '4px', display: 'block' }}>In minutes</small>}
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Email Signature{isCustomer && <InfoTip text="This text is added to the end of every AI-drafted email reply." />}</label>
                      <textarea className="input textarea" value={emailConfig?.email_signature || ''} onChange={(e) => setEmailConfig(prev => prev ? { ...prev, email_signature: e.target.value } : null)} rows={3} placeholder="Best regards,&#10;Your Company Name" />
                      <small>Appended to the end of every AI-generated draft.</small>
                    </div>
                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: emailConfigSaving ? 'not-allowed' : 'pointer', opacity: emailConfigSaving ? 0.6 : 1 }}>
                        <input
                          type="checkbox"
                          checked={Boolean(emailConfig?.cc_forwarder_on_forward)}
                          disabled={emailConfigSaving}
                          onChange={(e) => setEmailConfig(prev => prev ? { ...prev, cc_forwarder_on_forward: e.target.checked } : null)}
                        />
                        <span>
                          Keep forwarder copied on replies{isCustomer && <InfoTip text="Example: if accounting@joesplumbing.com forwards a customer's email to info@joesplumbing.com, the AI draft replies to the customer and adds accounting@joesplumbing.com as CC." />}
                        </span>
                      </label>
                      <small>Example: if accounting@joesplumbing.com forwards a customer's email to info@joesplumbing.com, the draft replies to the customer and adds accounting@joesplumbing.com as CC.</small>
                    </div>
                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: emailConfigSaving ? 'not-allowed' : 'pointer', opacity: emailConfigSaving ? 0.6 : 1 }}>
                        <input
                          type="checkbox"
                          checked={emailConfig?.exclude_newsletters ?? true}
                          disabled={emailConfigSaving}
                          onChange={(e) => setEmailConfig(prev => prev ? { ...prev, exclude_newsletters: e.target.checked } : null)}
                        />
                        <span>
                          Exclude newsletters{isCustomer && <InfoTip text="Skips newsletters and bulk emails so the AI only drafts replies for actionable messages." />}
                        </span>
                      </label>
                      <small>Skip newsletters and bulk emails so the AI only drafts replies for actionable messages.</small>
                    </div>
                    <div className="flex flex-center gap-3">
                      <button type="submit" className="btn btn-primary" disabled={emailConfigSaving}>
                        {emailConfigSaving ? <><span className="spinner spinner-sm" /> Saving...</> : 'Save Settings'}
                      </button>
                      {emailConfigMessage && <span className={emailConfigMessage.includes('success') ? 'success' : 'error'} style={{ fontWeight: 600 }}>{emailConfigMessage}</span>}
                      {!emailConfigMessage && emailConfigDirty.isDirty && <span className="unsaved-indicator">Unsaved changes</span>}
                    </div>
                  </form>
                </div>

                {/* Training Emails */}
                <div className="card" style={{ marginTop: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <div>
                      <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>{cl('Training Emails', 'Example Replies')}</h3>
                      <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>{cl('Provide example email exchanges so the AI learns your response style.', 'Show the AI how you typically reply to emails. Even 2\u20133 examples help it match your tone.')}</p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input type="file" ref={pdfFileRef} accept=".pdf" multiple style={{ display: 'none' }} onChange={handleTrainingPdfInput} />
                      <button onClick={() => { setShowAddTraining(!showAddTraining); setUploadResult(null); }} className="btn btn-primary">
                        {showAddTraining ? 'Cancel' : 'Add Manually'}
                      </button>
                    </div>
                  </div>
                  <div
                    onDragOver={handleTrainingPdfDragOver}
                    onDragLeave={handleTrainingPdfDragLeave}
                    onDrop={handleTrainingPdfDrop}
                    onClick={() => !uploadingTraining && pdfFileRef.current?.click()}
                    style={{
                      border: `2px dashed ${isTrainingPdfDragging ? 'var(--color-primary)' : 'var(--color-border)'}`,
                      borderRadius: 'var(--radius-lg)',
                      padding: '28px 20px',
                      textAlign: 'center',
                      backgroundColor: isTrainingPdfDragging ? 'var(--color-primary-light)' : 'var(--color-bg-secondary)',
                      cursor: uploadingTraining ? 'not-allowed' : 'pointer',
                      transition: 'all var(--transition-normal)',
                      marginBottom: '16px',
                    }}
                  >
                    {uploadingTraining ? (
                      <div>
                        <div className="spinner spinner-lg" style={{ margin: '0 auto 12px' }} />
                        <p style={{ color: 'var(--color-text-primary)', fontSize: '15px', fontWeight: 600, marginBottom: '4px' }}>
                          Processing {trainingPdfUploadCount} PDF{trainingPdfUploadCount === 1 ? '' : 's'}...
                        </p>
                        <p style={{ color: 'var(--color-text-secondary)', fontSize: '13px', margin: 0 }}>Extracting email exchanges for training data</p>
                      </div>
                    ) : (
                      <div>
                        <div style={{ width: '48px', height: '48px', margin: '0 auto 12px', backgroundColor: isTrainingPdfDragging ? 'var(--color-primary)' : 'var(--color-border)', borderRadius: 'var(--radius-lg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={isTrainingPdfDragging ? 'white' : 'var(--color-text-muted)'} strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17,8 12,3 7,8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                        </div>
                        <p style={{ color: 'var(--color-text-primary)', fontSize: '15px', fontWeight: 600, marginBottom: '4px' }}>
                          {isTrainingPdfDragging ? 'Drop training PDFs here' : 'Drag & drop training PDFs here'}
                        </p>
                        <p style={{ color: 'var(--color-text-secondary)', fontSize: '13px', margin: 0 }}>or click to choose PDF files</p>
                      </div>
                    )}
                  </div>
                  {uploadResult && (
                    <div style={{ marginBottom: '16px', padding: '12px 16px', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border-light)' }}>
                      {uploadResult.created > 0 && <p style={{ color: 'var(--color-success)', marginBottom: uploadResult.errors.length > 0 ? '8px' : '0' }}>{uploadResult.created} training email{uploadResult.created !== 1 ? 's' : ''} imported.</p>}
                      {uploadResult.errors.map((err, i) => <p key={i} style={{ color: 'var(--color-danger)', marginBottom: i < uploadResult.errors.length - 1 ? '4px' : '0' }}>{err}</p>)}
                      <button onClick={() => setUploadResult(null)} className="btn btn-ghost btn-small" style={{ marginTop: '8px' }}>Dismiss</button>
                    </div>
                  )}
                  {showAddTraining && (
                    <form onSubmit={handleAddTrainingEmail} style={{ marginBottom: 'var(--space-6)', padding: 'var(--space-5)', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border-light)' }}>
                      <div className="form-group">
                        <label>Subject (optional)</label>
                        <input type="text" className="input" value={newTrainingSubject} onChange={(e) => setNewTrainingSubject(e.target.value)} placeholder="e.g., Question about pricing" />
                      </div>
                      <div className="form-group">
                        <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          {cl('Incoming Email', "Customer's Email")}
                          <span>
                            <input type="file" ref={incomingFileRef} accept=".eml" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handleEmlUpload('incoming', e.target.files[0]); }} />
                            <button type="button" onClick={() => incomingFileRef.current?.click()} className="btn btn-ghost btn-small" disabled={uploadingTraining}>Upload .eml</button>
                          </span>
                        </label>
                        <textarea className="input textarea" value={newTrainingIncoming} onChange={(e) => setNewTrainingIncoming(e.target.value)} rows={4} placeholder="Paste the incoming email text or upload an .eml file..." required />
                      </div>
                      <div className="form-group">
                        <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          {cl('Your Response', 'Your Reply')}
                          <span>
                            <input type="file" ref={responseFileRef} accept=".eml" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handleEmlUpload('response', e.target.files[0]); }} />
                            <button type="button" onClick={() => responseFileRef.current?.click()} className="btn btn-ghost btn-small" disabled={uploadingTraining}>Upload .eml</button>
                          </span>
                        </label>
                        <textarea className="input textarea" value={newTrainingResponse} onChange={(e) => setNewTrainingResponse(e.target.value)} rows={4} placeholder="Paste how you responded or upload an .eml file..." required />
                      </div>
                      <button type="submit" className="btn btn-primary" disabled={trainingSaving || !newTrainingIncoming.trim() || !newTrainingResponse.trim()}>
                        {trainingSaving ? <><span className="spinner spinner-sm" /> Saving...</> : 'Save Training Email'}
                      </button>
                    </form>
                  )}
                  {trainingEmails.length === 0 ? (
                    <div className="alert alert-info"><span>{cl('No training emails yet. Add examples of how you typically respond to emails.', 'No example replies yet. Paste a real email exchange so the AI can learn how you write. Even 2\u20133 examples make a big difference.')}</span></div>
                  ) : (
                    <div>
                      {trainingEmails.map((te) => {
                        const isExpanded = expandedTrainingId === te.id;
                        return (
                          <div key={te.id} style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-3)', overflow: 'hidden' }}>
                            <div
                              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--space-4)', cursor: 'pointer' }}
                              onClick={() => setExpandedTrainingId(isExpanded ? null : te.id)}
                            >
                              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', transition: 'transform 0.15s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>&#9654;</span>
                                <div>
                                  <div style={{ fontWeight: 600, marginBottom: '2px' }}>{te.subject || te.incoming_body.substring(0, 60) + (te.incoming_body.length > 60 ? '...' : '')}</div>
                                  <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>{new Date(te.created_at).toLocaleDateString()}</div>
                                </div>
                              </div>
                              <button onClick={(e) => { e.stopPropagation(); handleDeleteTrainingEmail(te.id); }} className="btn btn-ghost btn-small" style={{ color: 'var(--color-danger)' }}>Delete</button>
                            </div>
                            {isExpanded && (
                              <div style={{ padding: '0 var(--space-4) var(--space-4)', borderTop: '1px solid var(--color-border-light)' }}>
                                <div style={{ marginTop: 'var(--space-3)' }}>
                                  <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', marginBottom: '6px' }}>Incoming Email</div>
                                  <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap', background: 'var(--color-bg-secondary)', padding: '12px', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-3)' }}>{te.incoming_body}</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', marginBottom: '6px' }}>Response</div>
                                  <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap', background: 'var(--color-bg-secondary)', padding: '12px', borderRadius: 'var(--radius-md)' }}>{te.response_body}</div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Preview / Demo */}
                <div style={{ marginTop: '24px' }}>
                  <EmailResponderInbox chatbotId={id!} chatbotName={chatbot?.name || ''} />
                </div>

                {/* Processed Emails & Drafts */}
                <div style={{ marginTop: '24px' }}>
                  <EmailDraftsLog chatbotId={id!} />
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Extract Confirmation Modal */}
      {showExtractConfirm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--color-bg-white)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-6)', maxWidth: '500px', width: '90%', boxShadow: 'var(--shadow-xl)' }}>
            <h3 style={{ margin: 0, marginBottom: 'var(--space-3)', fontSize: '18px', fontWeight: 600 }}>Replace or Append?</h3>
            <p style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-4)', lineHeight: 1.5 }}>You already have content in the Business Description. Would you like to replace or append?</p>
            <div style={{ background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', padding: 'var(--space-3)', marginBottom: 'var(--space-4)', maxHeight: '150px', overflow: 'auto', fontSize: '13px', border: '1px solid var(--color-border-light)' }}>
              <strong style={{ display: 'block', marginBottom: 'var(--space-2)' }}>Extracted content preview:</strong>
              {pendingExtractedContent.substring(0, 300)}{pendingExtractedContent.length > 300 ? '...' : ''}
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={handleExtractCancel}>Cancel</button>
              <button className="btn btn-secondary" onClick={handleExtractAppend}>Append</button>
              <button className="btn btn-primary" onClick={handleExtractReplace}>Replace</button>
            </div>
          </div>
        </div>
      )}

      {/* Unsaved changes notification */}
      {activeTab === 'chatbot' && (name !== savedValues.name || title !== savedValues.title || greeting !== savedValues.greeting || ctaLabel !== savedValues.ctaLabel || ctaUrl !== savedValues.ctaUrl) && (
        <div style={{ position: 'fixed', bottom: '24px', right: '24px', background: 'var(--color-bg-white)', backdropFilter: 'blur(10px)', border: '1px solid rgba(245,158,11,0.3)', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', borderRadius: '12px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px', maxWidth: '320px', zIndex: 1000, animation: 'slideInRight 0.3s ease-out' }}>
          <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--color-warning)', flexShrink: 0, animation: 'pulse 2s ease-in-out infinite' }} />
          <span style={{ fontSize: '14px', color: 'var(--color-text-primary)', fontWeight: 500 }}>Unsaved changes</span>
        </div>
      )}
      {activeTab === 'knowledge-base' && (instructions !== savedValues.instructions || websiteUrl !== savedValues.websiteUrl) && (
        <div style={{ position: 'fixed', bottom: '24px', right: '24px', background: 'var(--color-bg-white)', backdropFilter: 'blur(10px)', border: '1px solid rgba(245,158,11,0.3)', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', borderRadius: '12px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px', maxWidth: '320px', zIndex: 1000, animation: 'slideInRight 0.3s ease-out' }}>
          <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--color-warning)', flexShrink: 0, animation: 'pulse 2s ease-in-out infinite' }} />
          <span style={{ fontSize: '14px', color: 'var(--color-text-primary)', fontWeight: 500 }}>Unsaved changes</span>
        </div>
      )}

      <style>{`
        @keyframes slideInRight { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        .appearance-layout { display: grid; grid-template-columns: minmax(0, 1fr) minmax(320px, 420px); gap: 24px; align-items: start; }
        .appearance-controls { min-width: 0; }
        .appearance-preview { position: sticky; top: 96px; align-self: start; background: var(--color-bg-secondary); border: 1px solid var(--color-border-light); border-radius: var(--radius-lg); padding: 16px; min-width: 0; }
        .appearance-savebar { position: sticky; bottom: 16px; z-index: 20; display: flex; align-items: center; flex-wrap: wrap; gap: 12px; margin-top: 24px; padding: 12px; background: color-mix(in srgb, var(--color-bg-white) 92%, transparent); border: 1px solid var(--color-border); border-radius: var(--radius-lg); box-shadow: var(--shadow-lg); backdrop-filter: blur(10px); }
        .email-connection-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
        .email-connection-identity { display: flex; align-items: center; gap: 12px; min-width: 0; }
        .email-connection-actions { display: flex; align-items: center; justify-content: flex-end; flex-wrap: wrap; gap: 10px; }
        .email-test-actions { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; padding: 4px; background: var(--color-bg-white); border: 1px solid var(--color-border-light); border-radius: var(--radius-lg); }
        .email-test-button { min-width: 126px; }
        @media (max-width: 980px) {
          .appearance-layout { display: block; }
          .appearance-preview { position: static; margin-top: 24px; }
          .appearance-savebar { bottom: 12px; }
          .email-connection-header { flex-direction: column; }
          .email-connection-actions { width: 100%; justify-content: flex-start; }
          .email-test-actions { width: 100%; }
          .email-test-button { flex: 1 1 150px; }
        }
        @media (max-width: 560px) {
          .email-connection-actions { align-items: stretch; flex-direction: column; }
          .email-connection-actions > .btn,
          .email-test-actions,
          .email-test-button { width: 100%; }
          .email-test-actions { align-items: stretch; }
        }
      `}</style>

      {showWelcome && chatbot && (
        <WelcomeGuide
          chatbotName={chatbot.name}
          emailTabVisible={visibleTabs.includes('email-responder')}
          chatbotTabVisible={visibleTabs.includes('chatbot')}
          onClose={() => {
            setShowWelcome(false);
            if (user?.id) localStorage.setItem(`ni-welcome-seen-${user.id}`, 'true');
          }}
        />
      )}
    </div>
  );
}
